/* app/research-keywords.jsx - Keywords Planner: the site's KEYWORD REGISTER.

   Not a list of what Research captured. It holds every keyword, in use or not,
   and its value is the coverage picture only a complete register can give:
   pages with no focus keyword, keywords no page targets, and keywords more than
   one page competes for.

   THE TABLE PAGES AND THE COUNTS DO NOT. The register is read fifty rows at a
   time from GET /api/keywords, with status, fit, search and priority as query
   params so that a page is a page OF THE FILTERED SET. Every number on the
   screen comes from GET /api/keywords/summary, which counts in the database over
   those same filters: the status tabs, what the fit filter is hiding, how many
   keywords a classify run would still take, and the total the range under the
   table is stated against. None of them is arithmetic over the rows in hand,
   because a count that quietly meant "of the 50 you can see" would be worse than
   the unresponsive browser that paging fixed.

   Wired to /api/keywords (list/summary/status/delete/bulk/bulk-action/manual-add),
   scoped to the active site. A BULK ACT NAMES A SET, NOT A PAGE: POST
   /keywords/bulk-action takes either the ids ticked by hand or the filters the
   table is drawn with, and applies one statement to whatever they match, so
   "every row matching these filters" is a real control rather than a label over
   the fifty rows in view. On the destructive path the count the user was shown
   travels with the request and the server refuses if it matches a different one.

   Promote-to-plan posts /content-plans; "From Search Console"
   imports the query rows from /gsc/performance; Classify posts
   /classify/keywords for a given id list. Excel import parses the workbook in
   the browser (SheetJS) and posts it to /keywords/bulk in chunks; "Re-analyze
   all" still defers to Keywords 2.

   Classification is not a nicety on this screen, it is the gate. Facet assembly
   (backend/lib/assemble.js) pulls keywords into a piece by matching category or
   cluster, and a raw keyword has neither, so until a register is classified the
   pieces downstream cannot reach a brief. Hence two things here: the strip's
   unclassified card says what it blocks, and every run reports itself in full
   and offers to take the next batch rather than looping on its own.

   The coverage half reads GET /api/coverage/keywords, which answers over EVERY
   published page the last scan stored rather than over the rows this screen
   happens to hold, and POST /api/coverage/sync is the scan that fills it.

   Classification also answers whether a keyword belongs to this business at all
   (fit), which is why there is a Fit column and a filter that hides off-market
   and off-topic rows by default. MARK AND FILTER, NEVER DELETE: a hidden row is
   still in the register and its verdict can be overruled on the row, because a
   wrong verdict on a deleted row cannot be argued with. The filter says how many
   it is hiding, for the same reason. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
import { parseSheet, UNREADABLE } from '../lib/keyword-import.js';
(function () {
  const { useState, useRef, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const FD = (p) => React.createElement(window.MlBiHelpers.FacetDropdown, p);
  /* A finding as one line, its explanation in the drawer the line opens (see
     app/report-drawer.jsx). Resolved at RENDER time, like FD above, so this
     module does not depend on import order; if the module were ever missing,
     the children render inline rather than vanishing with the line. */
  const DetailLine = (p) => {
    const C = window.MlReportDrawer && window.MlReportDrawer.DetailLine;
    return C ? React.createElement(C, p) : React.createElement("div", { className: "ds-card" }, p.children);
  };
  const STATUS_COLOR = { pending: "var(--warning)", ready: "var(--success)", "in-use": "var(--info)", archived: "var(--text-muted)" };

  // backend keyword.status ↔ prototype status vocabulary
  const KW_STATUS_IN = { raw: "pending", classified: "ready", in_use: "in-use", archived: "archived" };
  const KW_STATUS_OUT = { pending: "raw", ready: "classified", "in-use": "in_use", archived: "archived" };
  const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");
  // backend keyword row → the table's row shape.
  //
  // There is no `used` here any more. It was mapped from `k.usedCount`, a field
  // no route has ever sent, so every row read "unused" and the Used filter
  // matched nothing at all. The real answer to "does anything target this" now
  // comes from the coverage scan, site-wide, further down.
  function mapKw(k) {
    return {
      id: k.id, kw: k.keyword || "", src: k.source_entity_type ? "research" : null,
      vol: k.volume || 0, impr: k.gsc_impressions || 0, pos: k.gsc_position || 0,
      clicks: k.gsc_clicks || 0, ctr: k.gsc_ctr != null ? Number(k.gsc_ctr) : null,
      intent: cap(k.intent), format: k.format || "-", cat: k.category || "-", prio: k.priority || "none",
      cluster: k.cluster || "", status: KW_STATUS_IN[k.status] || "pending",
      // null fit means nothing has ever judged this row, which is NOT the same
      // as 'unclear'. One is waiting for a run, the other has been looked at and
      // is waiting for a person, and they are never merged on this screen.
      fit: k.fit || null, fitWhy: k.fit_reason || null, fitByHand: k.fit_source === "human",
      /* WHICH SECTION HOLDS THIS ROW, and it is not decoration. At the All
         position the table lists rows from every connection of the website, and
         a per row edit has to be addressed to the connection that holds the row:
         the writes stay per connection on purpose, so sending the anchor's id
         for a row of another section would answer 404. null means the server did
         not say, and the code below falls back to the anchor rather than
         guessing: Number(null) is 0 and site 0 has never existed. */
      siteId: k.site_id === null || k.site_id === undefined || k.site_id === "" ? null : Number(k.site_id),
      /* The STORED language, which is the only language this app has. Not
         derived from the market and not read from the Business Profile: the
         overlap view rests on it, and a row carrying none is shown as carrying
         none. */
      lang: k.language ? String(k.language).trim() : null,
    };
  }

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  /* A stored language tag, as a person reads it.
   *
   * Intl.DisplayNames is the browser's own table, so this is not a hand kept
   * list that will be wrong about the next language. A tag it cannot name is
   * shown AS THE TAG rather than as a guess or a blank: the tag is what is
   * stored, and showing it is how somebody spots a typo in it. */
  const LANG_NAMES = (() => {
    try { return new Intl.DisplayNames(undefined, { type: "language" }); } catch { return null; }
  })();
  const langLabel = (tag) => {
    const t = String(tag || "").trim();
    if (!t) return null;
    try { return (LANG_NAMES && LANG_NAMES.of(t)) || t; } catch { return t; }
  };

  /* THE SWITCHER POSITION, AS THE SERVER'S PARAMETER.
   *
   * THREE STATES AND THEY ARE NOT TWO. `sectionId` undefined means this screen
   * was NOT TOLD which position the switcher is in, which is not the same as
   * being told it is at All. In that state every request is sent exactly as it
   * was sent before this change, so the screen keeps today's answer rather than
   * widening on a position nobody stated. null is the All position and a number
   * is one section.
   *
   * The route's rule is the same one from the other side: an absent section_id
   * is today's answer, `all` is the whole website. */
  const positionParam = (p, sectionId) => {
    if (sectionId === undefined) return p;
    p.set("section_id", sectionId === null ? "all" : String(sectionId));
    return p;
  };

  /* THE MARKET, AS THE SERVER'S PARAMETER, and it is a separate one.
   *
   * docs/MODULE-SCOPE.md 1.3: a market is a CONTROL ON this module, reached
   * with the switcher wherever it stands, and never a third kind of position in
   * the switcher. So it is its own parameter and it is sent only when the
   * shell gave this screen one, which it does only on a website with two or
   * more declared markets. A one market customer sends nothing new and gets
   * the answer they have always had.
   *
   * null and undefined both mean "do not send it", and that is deliberate: the
   * absent parameter is what every caller that existed before the selector
   * sends, and lib/register-scope.js answers it with today's filter. */
  const marketParam = (p, marketId) => {
    if (marketId === undefined || marketId === null) return p;
    const n = Number(marketId);
    if (!Number.isFinite(n) || n <= 0) return p;
    p.set("market_id", String(n));
    return p;
  };

  /* ---- THE MARKET CONTROL ------------------------------------------------
   *
   * WHAT IT IS AND WHAT IT IS NOT. docs/MODULE-SCOPE.md 1.3 settled the shape
   * before anything was built: a market is a control ON a module. It is not a
   * position of the switcher (which has exactly two kinds, All and one
   * section), and it is not a sidebar entry, because a new kind of an existing
   * thing belongs in that thing's existing selector.
   *
   * WITH FEWER THAN TWO MARKETS IT DRAWS NOTHING, and that is the first rule
   * and not a refinement. Most customers have one market, where "this website's
   * register" and "this market's register" are the same set and a control
   * offering one choice is a widget that can only be wrong. The shell sends no
   * market there either, so those screens are unchanged down to the request.
   *
   * WITH TWO OR MORE IT SAYS WHICH MARKET IT IS ANSWERING FOR, lets the
   * customer change it, and reports the two figures that keep it honest: how
   * many rows this market holds, and how many rows of this website are in NO
   * market at all. The second one is the one that may never be dropped. On the
   * live property it is the crawl-only front end's rows, whose section sits
   * above every declared market address, so nothing could place them; they are
   * real rows in nobody's register, and a screen that showed only the first
   * figure would be quietly reporting a shorter website.
   *
   * `answer` is what the SERVER said, never what was asked for. A market can
   * be asked for and refused (the proof is in lib/register-scope.js), and a
   * screen showing the asked-for market over rows that came from one connection
   * would be the exact quiet lie this module spends its length avoiding. */
  function MarketControl({ markets, marketId, onPick, answer, noun = "register" }) {
    const list = Array.isArray(markets) ? markets : [];
    if (list.length < 2) return null;

    const idOf = (m) => (m && m.id != null ? Number(m.id) : NaN);
    const nameOf = (m) => {
      const where = (m && (m.country_name || m.country)) || null;
      const lang = m && m.language ? langLabel(m.language) : null;
      return [where, lang].filter(Boolean).join(" · ") || `Market ${idOf(m)}`;
    };
    const addressOf = (m) => {
      const a = m && m.address ? String(m.address) : "";
      if (!a) return null;
      if (m.addressing === "path") return a.startsWith("/") ? a : `/${a}`;
      return a;
    };

    const sel = list.find(m => idOf(m) === Number(marketId)) || null;
    // What the server ANSWERED from, which is the only thing worth showing.
    const answered = answer && answer.selected != null ? Number(answer.selected) : null;
    const refused = sel != null && answered !== idOf(sel);
    const rows = answer && answer.rows != null ? answer.rows : null;
    const unplaced = answer && answer.unplaced != null ? answer.unplaced : null;

    return (
      <>
        <div className="ds-strip ds-strip--quiet" style={{ flexWrap: "wrap" }}>
          {/* "flag" and not a globe: the console's market line already uses it,
              and two glyphs for one subject reads as two subjects. */}
          <I name="flag" size={14} />
          <span>
            This website has {list.length} markets, and a {noun} belongs to one of them. Answering for
          </span>
          <select className="hds-select" style={{ maxWidth: 280, height: 30 }} value={sel ? String(idOf(sel)) : ""}
            onChange={(e) => onPick && onPick(Number(e.target.value))}
            title="Which market's register this screen is answering for. The switcher above still picks which section the acts that write run on.">
            {list.map(m => (
              <option key={idOf(m)} value={String(idOf(m))}>
                {nameOf(m)}{addressOf(m) ? ` (${addressOf(m)})` : ""}
              </option>
            ))}
          </select>
          {/* The figure the control is answerable for, from the read that
              produced the rows rather than from the length of a list. */}
          {rows != null && !refused ? (
            <span className="ml-propline__meta">{rows} in this market</span>
          ) : null}
          {/* AND THE ROWS IN NO MARKET AT ALL, which is a finding and not a
              rounding error. */}
          {unplaced ? (
            <span className="hds-badge hds-badge--warning"><span className="hds-badge__dot" />{unplaced} in no market</span>
          ) : null}
        </div>
        {unplaced ? (
          <div className="ds-strip ds-strip--quiet">
            <I name="info" size={14} />
            <span>
              {unplaced} {unplaced === 1 ? "row" : "rows"} of this website {unplaced === 1 ? "is" : "are"} in no
              market's register, so {unplaced === 1 ? "it is" : "they are"} not in this list. A row is placed in a
              market by the address its section publishes at: a piece that has not said where it publishes, or one
              that sits above every market's address like a front end at the root, cannot be placed. The property
              screen's market fill names each one and the rows it leaves blank.
            </span>
          </div>
        ) : null}
        {refused ? (
          <div className="ds-strip ds-strip--warning">
            <I name="alert-triangle" size={14} />
            <span>
              This is <b>one section</b>, not {sel ? nameOf(sel) : "that market"}'s register.
              {answer && answer.why ? ` ${answer.why}` : ""}
            </span>
          </div>
        ) : null}
      </>
    );
  }

  // ---- Fit: does a keyword belong to this business at all ----
  //
  // FIVE states, not four. The classifier reaches one of four verdicts; the
  // fifth is a row nothing has judged, which the column stores as NULL and which
  // this screen calls "not judged". Collapsing it into "unclear" would put rows
  // nobody has asked a question about into the queue a person is meant to work
  // through.
  //
  // off_market and off_topic are kept apart everywhere here because they lead to
  // different actions: an off-market term is the right trade in the wrong place
  // (nothing to do but drop it, or open that market), an off-topic one is a
  // different business entirely (usually a bad import). Both are hidden by
  // default and NEITHER is ever deleted.
  const FIT_LABEL = { relevant: "Relevant", off_market: "Off market", off_topic: "Off topic", unclear: "Unclear" };
  const FIT_COLOR = {
    relevant: "var(--success)", off_market: "var(--warning)", off_topic: "var(--danger)",
    unclear: "var(--info)", none: "var(--text-muted)",
  };
  // The two the default view hides. Named once, so the filter, the count of what
  // it hid and the sentence explaining it cannot drift apart: the count is the
  // summary's two buckets added up BY THIS LIST, so a third hidden verdict would
  // reach both at once.
  const HIDDEN_FITS = ["off_market", "off_topic"];
  // Who decided a verdict is its own column (fit_source in
  // backend/routes/classify.js), so this reads it rather than sniffing a prefix
  // out of the reason: that field is free text, and a reason a user happened to
  // begin with the marker would have frozen an AI verdict for good. A classify
  // run skips any row a person authored. The reason is now only the reason.
  const fitWhyText = (k) => String(k.fitWhy || "").trim();
  const FIT_OPTS = [
    ["relevant", "Relevant"], ["unclear", "Unclear"],
    ["off_market", "Off market"], ["off_topic", "Off topic"],
    ["none", "Not judged"],
  ];
  // What the hover says, which is the only place the model's one sentence is
  // readable. A row nobody has judged says so rather than showing an empty
  // reason, because "no verdict" and "a verdict with no reason" are different.
  function fitTitle(k) {
    if (!k.fit) return "Nothing has judged whether this keyword belongs to this business. Classify it, or set a verdict here.";
    const why = fitWhyText(k);
    return [
      FIT_LABEL[k.fit] || k.fit,
      k.fitByHand ? " (you set this, so a classify run will not overwrite it)" : "",
      why ? ": " + why : ". No reason was recorded.",
    ].join("");
  }
  // The default view keeps everything except the two verdicts that say "not this
  // business". Every other option is an explicit choice to look at one state.
  //
  // THE ROUTE IS THE AUTHORITY. GET /api/keywords applies exactly this, in SQL,
  // with IS DISTINCT FROM so the rows nothing has judged survive `keep`, and a
  // page is a page of the set it left. This mirror is kept for the one thing SQL
  // cannot reach: a verdict set on a row is written optimistically, so a keyword
  // marked off market under a view that hides off-market rows has to leave the
  // view now rather than at the next page load. It is a guard over the fifty
  // rows in hand and it is never a count.
  function fitMatches(k, view) {
    switch (view) {
      case "all": return true;
      case "keep": return !HIDDEN_FITS.includes(k.fit);
      case "unjudged": return !k.fit;
      default: return k.fit === view;
    }
  }
  // These values ARE the fit query param: the route reads `keep`, `all`,
  // `unjudged` and the four verdicts by those names, so nothing translates
  // between the dropdown and the query.
  const FIT_VIEW_OPTS = [
    ["keep", "Fit: hiding off market and off topic"],
    ["all", "Fit: everything"],
    ["relevant", "Fit: relevant only"],
    ["unclear", "Fit: unclear only"],
    ["unjudged", "Fit: not judged yet"],
    ["off_market", "Fit: off market only"],
    ["off_topic", "Fit: off topic only"],
  ];

  // A coverage picture has an age. One taken three weeks ago is not the current
  // state of the site and has to say so.
  function ago(ts) {
    if (!ts) return "";
    const d = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
  }

  const STATUS_OPTS = [["pending", "Pending"], ["ready", "Ready"], ["in-use", "In use"], ["archived", "Archived"]];
  const intentCls = { Informational: "intent-info", Commercial: "intent-commercial", Transactional: "intent-transactional", Navigational: "intent-navigational" };

  function statusBadgeTone(s) {
    const { Badge } = window.HuddleDesignSystem_775113;
    const map = { pending: ["warning", "Pending"], ready: ["success", "Ready"], "in-use": ["info", "In use"], archived: ["neutral", "Archived"] };
    const [tone, l] = map[s];
    return <Badge tone={tone} dot={s !== "archived"}>{l}</Badge>;
  }

  // ---- Import sub-view ----
  // The parsing rules (finding the header row, volume formats, dedup) live in
  // lib/keyword-import.js, where they are tested.

  const readFileBuffer = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result));
    r.onerror = () => reject(new Error("The file could not be read."));
    r.readAsArrayBuffer(file);
  });

  const MAX_MB = 10;
  const CHUNK = 500; // rows per POST - keeps each insert well inside pg's parameter ceiling

  function ImportView({ onBack, site, onSaved }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [stage, setStage] = useState("idle"); // idle | drag | reading | parsed | importing | done
    const [file, setFile] = useState(null);
    const [parsed, setParsed] = useState(null);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);
    const [sent, setSent] = useState(0);
    const inputRef = useRef(null);

    const take = async (f) => {
      if (!f) return;
      setErr(""); setDone(null); setParsed(null); setFile(f);
      if (f.size > MAX_MB * 1024 * 1024) { setStage("idle"); setErr(`That file is ${(f.size / 1048576).toFixed(1)} MB - the limit is ${MAX_MB} MB.`); return; }
      setStage("reading");
      try {
        const res = await parseSheet(await readFileBuffer(f));
        setParsed(res); setStage("parsed");
      } catch (e) {
        setStage("idle");
        // Only messages we wrote ourselves are fit to show. Anything else is a
        // crash inside the parser or a failed chunk fetch, and its text tells
        // the person holding the file nothing they can act on.
        setErr(e && e.userFacing ? e.message : UNREADABLE);
      }
    };

    const reset = () => { setStage("idle"); setParsed(null); setFile(null); setErr(""); setDone(null); setSent(0); if (inputRef.current) inputRef.current.value = ""; };

    /* Imported rows land as Pending, exactly like a manual add. Classifying
       2,000 keywords is a real Claude spend, so it stays the deliberate act it
       is on the table screen (which prices the run first) instead of riding
       along invisibly with an upload. */
    const runImport = async () => {
      if (!parsed || siteId == null) return;
      setStage("importing"); setErr(""); setSent(0);
      const all = parsed.rows;
      let inserted = 0, skipped = 0, duplicates = 0;
      /* WHICH REGISTER THE DUPLICATE CHECK ASKED, which the owner moved to the
         MARKET on 2026-10-20. It matters on screen and not only in a log: a
         market wide register legitimately rejects far more of a file than a per
         connection one did, so "395 already in the register" has to say WHICH
         register, or an import that brought in 100 of 500 reads as broken.
         Taken from the last chunk because every chunk resolves the same scope
         for the same connection, and null until one answers: this is never
         defaulted to "market", which would claim a scope the server may have
         refused. */
      let dupScope = null, dupWhy = null;
      try {
        for (let i = 0; i < all.length; i += CHUNK) {
          const batch = all.slice(i, i + CHUNK).map(r => ({ keyword: r.kw, volume: r.vol == null ? undefined : r.vol }));
          const r = await api(`/keywords/bulk`, { method: "POST", body: JSON.stringify({ site_id: siteId, keywords: batch }) });
          inserted += (r && r.inserted) || 0;
          skipped += (r && r.skipped) || 0;
          duplicates += (r && r.duplicates) || 0;
          if (r && r.duplicates_scope) { dupScope = r.duplicates_scope; dupWhy = r.duplicates_why || null; }
          setSent(Math.min(i + CHUNK, all.length));
        }
        setDone({ inserted, skipped, duplicates, parsed: all.length, dupScope, dupWhy });
        setStage("done");
        onSaved && onSaved();
      } catch (e) {
        setStage("parsed");
        setErr(`Imported ${inserted} of ${all.length} before it failed: ${e.message || "the request was rejected."}`);
      }
    };

    const busy = stage === "importing";
    return (
      <div className="bi-page" style={{ maxWidth: 820 }}>
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to Keywords Planner</button>
        <h1 className="bi-title" style={{ marginBottom: 16 }}><span className="bi-title__icon"><I name="upload-cloud" size={20} /></span>Import keywords</h1>

        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={e => take(e.target.files && e.target.files[0])} />

        {err && <div style={{ marginBottom: 12, color: "var(--danger)", fontSize: "var(--text-sm)", display: "flex", alignItems: "center", gap: 6 }}><I name="alert-triangle" size={14} />{err}</div>}

        {stage === "done" && done ? (
          <div>
            {/* HOW MANY OF HOW MANY, AND WHY, every time. An import that says
                only what it added leaves a person counting rows to find out
                what happened to the rest, and with a market wide duplicate
                check "the rest" can be most of the file. */}
            <div className="research-cost-note" style={{ marginTop: 0, marginBottom: 16 }}>
              <I name="check" size={14} style={{ color: "var(--success)" }} />
              {plural(done.parsed, "keyword", "keywords")} in the file, {num(done.inserted)} saved as Pending
              {done.duplicates
                ? `, ${num(done.duplicates)} already in ${done.dupScope === "market" ? "this website's register, which its sections share" : "this section's register"}`
                : ""}
              {done.skipped ? `, ${num(done.skipped)} with no keyword text` : ""}. Classify them from the table when you
              are ready.
            </div>
            {/* The scope the server REFUSED to widen to, said plainly rather
                than left as a smaller number nobody can explain. */}
            {done.duplicates > 0 && done.dupScope === "connection" && (
              <div className="ds-strip ds-strip--quiet" style={{ marginBottom: 16 }}>
                <I name="info" size={14} />
                <span>
                  Duplicates were checked against <b>this section only</b>, not the whole website.
                  {done.dupWhy ? ` ${done.dupWhy}` : ""}
                </span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="bi-btn" onClick={reset}>Import another file</button>
              <button className="bi-btn bi-btn--primary" onClick={onBack}>Back to Keywords Planner</button>
            </div>
          </div>
        ) : stage === "parsed" || stage === "importing" ? (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <span className="bi-badge bi-badge--validated"><I name="check" size={11} />{file ? file.name : "export"}</span>
              <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                Preview - {plural(parsed.rows.length, "keyword", "keywords")} found · showing first 4
                {parsed.dupes ? ` · ${parsed.dupes} duplicate ${parsed.dupes === 1 ? "row" : "rows"} merged` : ""}
                {parsed.blank ? ` · ${parsed.blank} blank skipped` : ""}
              </span>
            </div>
            <div className="kw-table-wrap" style={{ marginBottom: 16 }}>
              <table className="kw-table">
                <thead><tr><th>Keyword</th><th>Volume</th><th>Competition</th><th>Bid low</th><th>Bid high</th></tr></thead>
                <tbody>{parsed.rows.slice(0, 4).map((r, i) => <tr key={i}><td className="kw-kw">{r.kw}</td><td className="kw-num">{r.vol == null ? "-" : r.vol.toLocaleString()}</td><td className="kw-num">{r.cmp}</td><td className="kw-num">{r.bl}</td><td className="kw-num">{r.bh}</td></tr>)}</tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="bi-btn" disabled={busy} onClick={reset}>Choose another file</button>
              <button className="bi-btn bi-btn--primary" disabled={busy || siteId == null} onClick={runImport}>{busy ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Importing {sent.toLocaleString()} / {parsed.rows.length.toLocaleString()}…</> : <><I name="check" size={16} />Import {plural(parsed.rows.length, "keyword", "keywords")}</>}</button>
            </div>
            <div className="research-cost-note" style={{ marginTop: 12 }}><I name="info" size={14} style={{ color: "var(--text-muted)" }} />
              {siteId == null
                ? "Pick a site first - keywords belong to one site's register."
                : "Added as Pending - classify them from the table afterwards, so the Claude spend stays yours to trigger."}
            </div>
          </div>
        ) : (
          <div className={"upload-zone" + (stage === "drag" ? " is-dragover" : "")}
            onClick={() => stage !== "reading" && inputRef.current && inputRef.current.click()}
            onDragOver={e => { e.preventDefault(); setStage("drag"); }}
            onDragLeave={() => setStage("idle")}
            onDrop={e => { e.preventDefault(); setStage("idle"); take(e.dataTransfer.files && e.dataTransfer.files[0]); }}>
            <span className="upload-zone__icon">{stage === "reading" ? <span className="ml-spinner ml-spinner--sm" style={{ width: 24, height: 24 }} /> : <I name="upload-cloud" size={24} />}</span>
            <div className="upload-zone__title">{stage === "reading" ? "Reading the file…" : <>Drop your export or <b>browse</b></>}</div>
            <div className="upload-zone__hint">Google Keyword Planner exports · .xlsx, .xls or .csv · {stage === "drag" ? "release to upload" : `up to ${MAX_MB} MB`}</div>
          </div>
        )}
      </div>
    );
  }

  // ---- Manual sub-view ----
  function ManualView({ onBack, site, onSaved }) {
    const { Textarea } = window.HuddleDesignSystem_775113;
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [busy, setBusy] = useState(false);
    const [text, setText] = useState("");
    const add = async () => {
      const list = text.split(/\n/).map(s => s.trim()).filter(Boolean);
      if (!list.length || siteId == null) { onBack(); return; }
      setBusy(true);
      try { await api(`/keywords/bulk`, { method: "POST", body: JSON.stringify({ site_id: siteId, keywords: list.map(k => ({ keyword: k })) }) }); onSaved && onSaved(); }
      catch { /* */ }
      finally { setBusy(false); onBack(); }
    };
    return (
      <div className="bi-page" style={{ maxWidth: 680 }}>
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to Keywords Planner</button>
        <h1 className="bi-title" style={{ marginBottom: 16 }}><span className="bi-title__icon"><I name="plus" size={20} /></span>Add keywords manually</h1>
        <div className="bi-detail-section"><div className="bi-detail-section__body" style={{ paddingTop: 16 }}>
          <label className="bi-field__label">Keywords <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>· one per line</span></label>
          <Textarea rows={7} value={text} onChange={e => setText(e.target.value)} placeholder={"content operations platform\nseo workflow automation\nmulti-site content management"} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, gap: 12, flexWrap: "wrap" }}>
            <span className="research-cost-note" style={{ marginTop: 0 }}><I name="info" size={14} style={{ color: "var(--text-muted)" }} />Added as Pending - classify them later.</span>
            <button className="bi-btn bi-btn--primary" disabled={busy || !text.trim()} onClick={add}>{busy ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />Adding…</> : <><I name="plus" size={16} />Add keywords</>}</button>
          </div>
        </div></div>
      </div>
    );
  }

  // ---- The vocabulary a run classifies into ----

  /**
   * What the classifier is given to classify INTO, and where it came from.
   *
   * WHY THIS IS ON SCREEN BEFORE THE RUN. The tokens are spent either way, so
   * what a run will classify into has to be visible first. THREE FACETS, RANKED
   * BY WHAT EACH ONE DECIDES, because three paragraphs of the same shape would
   * bury the one that matters:
   *
   *   cluster  - a cluster groups the keywords one page should serve, and facet
   *              assembly (backend/lib/assemble.js) ranks a shared cluster ABOVE
   *              a shared category when it pulls keywords into a piece. It is
   *              therefore the field that decides whether anything groups at
   *              all, and until now the classifier never wrote one: 0 of the 14
   *              keywords on the live site carry a cluster. It leads here.
   *   category - with nothing to classify into the model invents a bucket per
   *              row, and on the live site that produced 34 categories over 38
   *              items, 30 of them holding one thing. Second, and the only facet
   *              whose absence is a reason not to run.
   *   tags     - 90 over 38 items, 74 of them on exactly one item, which is NOT
   *              the fault: tags are supposed to be granular. Inventing a fresh
   *              one where an existing one fits is. A weaker constraint than the
   *              one on categories, so it reads as a trailing note rather than a
   *              third block of equal weight.
   *
   * GET /classify/vocabulary answers all three, and the classify POST reports
   * what it applied for each. Both are read through readVocab, tolerantly: the
   * names and the count are what this screen says, and which key they arrive
   * under is the backend's business. A facet the payload never mentions reads as
   * null, which means "not reported" and is never rendered as "none": one is a
   * server that does not say, the other is a site that genuinely has none of
   * that facet, and a site can easily have categories and no tags.
   */
  const SOURCE_SAYS = {
    wordpress: "from WordPress", wp: "from WordPress", wp_category: "from WordPress",
    category: "from WordPress", categories: "from WordPress",
    suggestion: "an approved suggestion", suggestions: "an approved suggestion",
    taxonomy_suggestion: "an approved suggestion", taxonomy_suggestions: "an approved suggestion",
    approved: "an approved suggestion", approved_suggestions: "from approved suggestions",
  };
  const sourceSays = (k) => SOURCE_SAYS[String(k).toLowerCase()] || String(k).replace(/_/g, " ");
  // A per-source tally on the envelope ({ wordpress: 6, suggestions: 2 }),
  // which is the same fact as a source per name and which the endpoint is free
  // to send instead. A source may also arrive as its own { ok, count, error }
  // record, and either way only what it CONTRIBUTED is named: a source that
  // failed adds nothing here rather than a confident "0 from WordPress".
  function declaredSources(box) {
    const d = box && box.sources;
    if (!d || typeof d !== "object" || Array.isArray(d)) return [];
    return Object.entries(d)
      .map(([k, v]) => [k, Number(v && typeof v === "object" ? v.count : v)])
      .filter(([, n]) => Number.isFinite(n) && n > 0)
      .map(([k, n]) => `${n} ${sourceSays(k)}`);
  }

  // Where one facet's answer may arrive. The endpoint may send a plain list
  // beside the categories, an envelope per facet carrying its own count,
  // sources and ok/error, or a bare count with no list at all.
  const FACET_KEYS = { categories: ["categories", "category"], clusters: ["clusters", "cluster"], tags: ["tags", "tag"] };
  const LIST_KEYS = ["names", "terms", "list", "values"];
  const numOr = (...vs) => { for (const v of vs) { if (v == null || v === "") continue; const n = Number(v); if (Number.isFinite(n)) return n; } return null; };
  const arrIn = (o, keys) => { for (const k of keys) if (Array.isArray(o && o[k])) return o[k]; return null; };

  /**
   * ONE facet of the vocabulary as { names, total, sources, error }, or null
   * when the payload never mentions that facet at all.
   *
   * `error` is that facet's OWN failed read, which the endpoint reports per
   * source. It is a different answer from a total of 0 and must never be shown
   * as one: a site with no tags and a tag list nobody could read lead to
   * opposite conclusions.
   */
  function readFacet(raw, facet) {
    if (!raw || typeof raw !== "object") return null;
    // `vocabularies` is the uniform map the endpoint restates all three under,
    // and it is read FIRST because the classify responses put the category facet
    // alone under `vocabulary`: looking there for tags would find nothing and
    // report a facet the run really did apply as one the server never mentioned.
    const env  = Array.isArray(raw) ? raw : (raw.vocabularies || raw.vocabulary || raw.vocab || raw);
    const keys = FACET_KEYS[facet];
    const cat  = facet === "categories";
    // A bare array payload IS the category list, and says nothing about the rest.
    if (Array.isArray(env) && !cat) return null;

    let held = null;
    if (Array.isArray(env)) held = env;
    else for (const k of keys) if (env[k] != null) { held = env[k]; break; }
    const own  = held && !Array.isArray(held) && typeof held === "object" ? held : null;
    const list = Array.isArray(held) ? held : (own ? (arrIn(own, keys) || arrIn(own, LIST_KEYS)) : null);
    const flat = Array.isArray(env) ? {} : env;

    // The envelope's own total counts CATEGORIES, which are the payload's
    // subject. A tag or cluster count has to be stated for itself, or this
    // would report the category count as if it were theirs.
    const stated = own ? numOr(own.total, own.count)
      : cat ? numOr(flat.total, flat.count)
      : numOr(...keys.map(k => flat[k + "_total"]), ...keys.map(k => flat[k + "_count"]));
    const box = own || (cat ? flat : null);
    let said = own || (cat ? flat : null);
    if (!said) for (const k of keys) if (flat[k + "_ok"] != null || flat[k + "_error"] != null) { said = { ok: flat[k + "_ok"], error: flat[k + "_error"] }; break; }
    const error = (said && (said.ok === false || said.error))
      ? (String(said.error || "").trim() || "The server did not say why.")
      : null;

    if (!list) {
      // A count with no list is still an answer, and a count of zero is the one
      // that matters most, so it is read rather than dropped as unreported. A
      // facet with no list, no count and no error was not reported at all, and
      // guessing "none" for it would be the invention this exists to prevent.
      if (stated == null) return error ? { names: [], total: 0, sources: [], error } : null;
      return { names: [], total: stated, sources: box ? declaredSources(box) : [], error };
    }
    const names = [];
    const bySource = new Map();
    for (const c of list) {
      const name = typeof c === "string" ? c.trim() : String((c && (c.name || c.term || c.label || c[keys[1]])) || "").trim();
      if (!name) continue;
      names.push(name);
      const from = (c && typeof c === "object" && (c.source || c.from || c.origin)) || null;
      if (from) { const said2 = sourceSays(from); bySource.set(said2, (bySource.get(said2) || 0) + 1); }
    }
    const counted = [...bySource.entries()].map(([l, n]) => `${n} ${l}`);
    // A total the endpoint states wins only when it exceeds what it listed: a
    // capped list must not read as the whole vocabulary, and a total below the
    // names on screen would contradict them.
    return {
      names,
      total  : stated != null && stated > names.length ? stated : names.length,
      sources: counted.length ? counted : (box ? declaredSources(box) : []),
      error,
    };
  }

  /**
   * The whole answer, facet by facet. Always an object: it is the FACETS inside
   * that are null when the payload does not report them, so nothing asking
   * about tags can ever be handed the categories instead.
   */
  function readVocab(raw) {
    return {
      clusters  : readFacet(raw, "clusters"),
      categories: readFacet(raw, "categories"),
      tags      : readFacet(raw, "tags"),
    };
  }

  // The four answers ONE facet can give, decided in one place so nothing can
  // render a failed read as an empty one:
  //   unreported - the payload never mentioned this facet
  //   failed     - its read failed, so what the site has is not known
  //   none       - the site genuinely has none of it
  //   some       - a real vocabulary
  function facetState(f) {
    if (!f) return "unreported";
    if (f.error && !f.total) return "failed";
    return f.total ? "some" : "none";
  }
  // A vocabulary that IS there but whose read half failed is short by an unknown
  // amount, and saying so is the difference between a list and a complete one.
  const shortBy = (f) => (f && f.total && f.error ? ` One source did not answer, so this list may be short: ${f.error}` : "");

  /** Names on screen before the rest is counted rather than listed. */
  const VOCAB_SHOWN = 8;
  function VocabNames({ v }) {
    const shown = v.names.slice(0, VOCAB_SHOWN);
    if (!shown.length) return null;
    return (
      <div className="ds-card__sample">
        {shown.map((n, i) => <span className="ds-chip" key={n + ":" + i}>{n}</span>)}
        {v.total > shown.length && <span className="ds-card__note">and {v.total - shown.length} more</span>}
      </div>
    );
  }

  // Said once, so the warning before a run and the finding after it cannot
  // drift into two different explanations of the same thing.
  const NO_VOCAB_WHY = "There is nothing to classify into, so the classifier invents a bucket per keyword. A bucket only one keyword holds groups nothing: facet assembly pulls material into a piece by SHARED cluster or category, so an invented category leaves the cluster doing all of the work by itself.";
  const NO_VOCAB_FIX = "Taxonomy Library proposes at most 8 broad categories drawn from this site's own content, and creates them in WordPress when you approve them. That is the vocabulary this would then classify into.";

  /**
   * The way to the screen that MAKES a vocabulary.
   *
   * `taxonomy-suggestions` is the id main.jsx routes on and the one data.jsx
   * declares, and the label and its nav group are read from that same nav data,
   * so a renamed item renames itself here too.
   *
   * A missing `go` is not treated as a reason to hide the way out, and not as a
   * reason to render a button either: with no navigator the destination is named
   * in words instead. A control that would do nothing when pressed is the one
   * thing this must never be.
   */
  const TAXONOMY_SCREEN = "taxonomy-suggestions";
  function taxonomyLabel() {
    for (const g of ((window.MlData && window.MlData.NAV_GROUPS) || [])) {
      for (const it of (g.items || [])) if (it.id === TAXONOMY_SCREEN) return it.label;
    }
    return "Taxonomy Library";
  }
  function taxonomyPath() {
    for (const g of ((window.MlData && window.MlData.NAV_GROUPS) || [])) {
      for (const it of (g.items || [])) if (it.id === TAXONOMY_SCREEN) return `${g.label ? g.label + " · " : ""}${it.label}`;
    }
    return "Content · Taxonomy Library";
  }
  function TaxonomyLink({ onGo }) {
    if (!onGo) return <span className="ds-card__note">Open {taxonomyPath()} from the sidebar to build one</span>;
    return <button className="bi-btn bi-btn--sm" onClick={onGo}><I name="sparkles" size={14} />Open {taxonomyLabel()}</button>;
  }

  /**
   * What this run will classify into, in the confirm dialog, before the tokens.
   *
   * ONE brief covering all three facets, ranked, rather than a second brief
   * bolted beside the first. Two of the four answers are screen-wide (still
   * reading, and a read that failed outright) and the other two are per facet (a
   * facet the server did not report, and a site that genuinely has none of it),
   * because a site can easily have categories and no tags.
   *
   * Only an empty CATEGORY vocabulary is a reason not to run, and only it is
   * offered the way out. Nothing in the Taxonomy Library makes a cluster, so
   * offering that button beside an empty cluster list would be a control that
   * cannot do what it says; and an empty cluster list is not a blocker anyway,
   * since writing the first clusters is what this run is for.
   *
   * The caller wraps this in .ds-brief, which spaces the blocks, so each one is
   * a plain sibling and carries no margin of its own.
   */
  function VocabBrief({ vocab, onTaxonomy }) {
    if (vocab.loading) return <div>Still reading which clusters, categories and tags this site classifies into.</div>;
    if (vocab.error) {
      return (
        <div>
          Could not read which clusters, categories and tags this site classifies into: {vocab.error} The run would
          still use whatever the server holds; this screen cannot say what that is.
        </div>
      );
    }
    const v  = vocab.data || {};
    const cl = v.clusters, cat = v.categories, tg = v.tags;

    // The cluster leads: assembly reaches for it BEFORE the category, and no
    // keyword here has ever carried one. Its sources are deliberately not named
    // the way the categories' are: a cluster has only ever existed in the
    // keyword register, so a breakdown could only ever restate the total.
    const cluster = () => {
      switch (facetState(cl)) {
        case "some": return (
          <div>
            <b>The cluster is what decides whether these group at all.</b> Facet assembly asks for a shared cluster
            before it asks for a shared category, and this run fills one in wherever a keyword has none, leaving any
            you grouped by hand alone. It has the {plural(cl.total, "cluster", "clusters")} the keywords here already
            carry to reuse.{shortBy(cl)}
            <VocabNames v={cl} />
          </div>
        );
        case "none": return (
          <div>
            <b>Nothing on this site carries a cluster yet, so this run writes the first ones.</b> Facet assembly asks
            for a shared cluster before it asks for a shared category, so until one exists its strongest way of
            grouping is switched off. There is none to reuse: the run groups these keywords among themselves.
          </div>
        );
        case "failed": return (
          <div>
            Could not read which clusters the keywords here already carry: {cl.error} The run still fills one in
            wherever a keyword has none; this screen cannot say which existing ones it can reuse.
          </div>
        );
        default: return (
          <div>
            The server did not say which clusters the keywords here already carry, so this screen cannot show what the
            run can reuse. It is the field facet assembly asks for before the category.
          </div>
        );
      }
    };

    const category = () => {
      switch (facetState(cat)) {
        case "some": return (
          <div>
            It classifies into the {plural(cat.total, "category", "categories")} this site already uses
            {cat.sources.length ? ` (${cat.sources.join(", ")})` : ""}. A shared category is the next thing assembly
            tries, so keywords landing in these can still be pulled into a piece when no cluster matches.{shortBy(cat)}
            <VocabNames v={cat} />
          </div>
        );
        case "none": return (
          <>
            <div><b>This site has no categories to classify into.</b> {NO_VOCAB_WHY} {NO_VOCAB_FIX}</div>
            <div><TaxonomyLink onGo={onTaxonomy} /></div>
          </>
        );
        case "failed": return (
          <div>
            Could not read which categories this site classifies into: {cat.error} The run would still use whatever the
            server holds; this screen cannot say what that is.
          </div>
        );
        default: return <div>The server did not say which categories this site classifies into, so this screen cannot show them before the run.</div>;
      }
    };

    // Tags trail as a note, and say a different thing from the category line on
    // purpose: many tags is not the fault, so this is a preference for reuse
    // rather than the hard constraint above it.
    const tags = () => {
      switch (facetState(tg)) {
        case "some": return <div className="ds-card__note">Tags: the run can reuse the {tg.total} this site already uses. Tags are meant to be granular, so a new one is not a fault by itself; reaching for one that already fits before inventing a near-duplicate is all this asks.</div>;
        case "none": return <div className="ds-card__note">Tags: this site has none yet, so every tag this run writes is a new one. Nothing assembles on tags, so that is housekeeping rather than a blocker.</div>;
        case "failed": return <div className="ds-card__note">Tags: could not read which ones this site already uses ({tg.error}), so this screen cannot say what the run can reuse.</div>;
        default: return <div className="ds-card__note">Tags: the server did not say which ones this site already uses.</div>;
      }
    };

    return <>{cluster()}{category()}{tags()}</>;
  }

  /**
   * What the run decided about BELONGING, which is a separate question from
   * what it decided about format and category and is reported separately.
   *
   * Numbers that must not merge: what it judged, what it left alone because a
   * person had already decided, what came back without a readable verdict (still
   * unjudged, not "unclear"), and how much of the register has still never been
   * judged. A run that judged 200 of 1,900 has to read as one.
   *
   * A run that judged NOTHING because the site has no business profile is not a
   * failure and not a clean sweep: it says which it was, in the server's own
   * words, since the server is what knows why.
   */
  function FitReport({ fit }) {
    if (!fit) return null;
    if (!fit.asked) return <p className="ds-card__detail ds-card__note">No keyword was judged for belonging. {fit.why}</p>;
    const c = fit.counts || {};
    const split = ["relevant", "unclear", "off_market", "off_topic"]
      .filter(k => c[k]).map(k => `${c[k]} ${FIT_LABEL[k].toLowerCase()}`);
    const bank = fit.bank || {};
    return (
      <>
        <p className="ds-card__detail">
          {fit.judged
            ? `Judged ${plural(fit.judged, "keyword", "keywords")} against this site's business profile: ${split.join(", ")}.`
            : "Nothing new was judged against this site's business profile."}
          {fit.kept_by_hand ? ` ${plural(fit.kept_by_hand, "verdict", "verdicts")} you had set by hand were left exactly as they were.` : ""}
          {fit.unreadable ? ` ${plural(fit.unreadable, "row", "rows")} came back without a verdict this could read, so they stay unjudged rather than being called unclear.` : ""}
          {" Nothing was deleted or archived: an off-market keyword is hidden by the fit filter below and is still in the register."}
        </p>
        {fit.why && <p className="ds-card__detail ds-card__note">{fit.why}</p>}
        {bank.ok === false
          ? <p className="ds-card__detail ds-card__note">How many keywords are still unjudged could not be read: {bank.error}</p>
          : bank.unjudged > 0
            ? <p className="ds-card__detail ds-card__note">{bank.unjudged} of the {bank.total} keywords in this register have still never been judged. Classify again to work through them.</p>
            : null}
      </>
    );
  }

  // ---- Classification ----

  /**
   * How many keywords one run may classify.
   *
   * The server is the authority (MAX_BATCH in backend/routes/classify.js caps
   * every call at 60 and reports what it did not reach). This copy exists only
   * so the screen can say how many the next run will take, and it is used for
   * wording, never to decide anything.
   */
  const PER_RUN = 60;

  /**
   * What one classification run actually did, kept on screen until dismissed.
   *
   * Three numbers, never merged into one: `classified` landed in the database,
   * `skipped` were inside the batch but the AI reply was cut short before
   * reaching them (so they keep the facets they had), and `remaining` were past
   * the cap and were never sent at all. A run that classified nothing is
   * reported as a failure, because "0 classified" is not a quiet success.
   *
   * Continuing is a button and never a loop: each run is an AI call the account
   * pays for, so the screen says how many are left and waits to be asked.
   *
   * Three outcomes that are easy to blur are kept apart. A request that FAILED
   * is a problem and says what the server said. A run that was asked for
   * NOTHING never reached the AI and is not a failure. A run that ran and
   * classified NOTHING is a failure, whatever the response code was.
   *
   * It renders only after a run. A register with nothing to classify says
   * nothing here.
   *
   * `run.vocab` is what the RUN reported it applied, read from the response
   * rather than from the vocabulary this screen loaded earlier: those are two
   * different facts and only the first one describes these keywords. Each facet
   * inside it is null when the server did not report that facet, and a facet
   * reported by nobody says NOTHING here rather than claiming there was none of
   * it.
   *
   * On screen it is ONE LINE: the outcome with its numbers, which opens a
   * drawer holding the full report (window.MlReportDrawer). The report lost
   * none of its sentences in the fold; it stopped sitting on top of the table
   * it reports on.
   */
  function ClassifyRunReport({ run, busy, onContinue, onDismiss, onTaxonomy }) {
    const { ReportStrip } = window.MlReportDrawer;
    const left = (run.next || []).length;
    const vocab = run.vocab || {};
    const cl = vocab.clusters, cat = vocab.categories, tg = vocab.tags;
    const did = run.classified > 0;
    const failed = !!run.error || (!run.empty && run.classified === 0);
    // The strip's one line carries the numbers a glance needs (what landed,
    // what the reply skipped, what is still waiting); everything else waits in
    // the drawer. Both read the same run object, so they cannot disagree.
    const base = run.error ? "Classification failed"
      : run.empty ? "Nothing to classify"
      : run.classified === 0 ? "This run classified nothing"
      : `Classified ${plural(run.classified, "keyword", "keywords")}`;
    const bits = [];
    if (!run.error && !run.empty) {
      if (run.skipped) bits.push(`${run.skipped} skipped`);
      if (left) bits.push(`${left} still waiting`);
    }
    const title = base + (bits.length ? `, ${bits.join(", ")}` : "");
    /* A run that classified into nothing is not a finished job, whatever its
       count says, so it never gets the settled look. */
    const tone = failed ? "problem" : (left || facetState(cat) === "none") ? "todo" : "opportunity";
    return (
      <ReportStrip title={title} tone={tone} onDismiss={onDismiss}>
        {run.error ? <p className="ds-card__detail">{run.error}</p> : run.empty ? <p className="ds-card__detail">{run.empty}</p> : (
          <p className="ds-card__detail">
            {run.matchedNothing
              ? "The register holds none of the keywords this run asked for, so nothing was sent and nothing was charged. Reload the screen and try again."
              : `${run.classified} of the ${plural(run.asked, "keyword", "keywords")} it asked for came back classified.`}
            {run.skipped ? ` ${plural(run.skipped, "row", "rows")} in the batch kept the facets they already had: the AI reply stopped before reaching them.` : ""}
            {run.remaining ? ` ${run.remaining} were past the ${PER_RUN} one run may take and were never sent.` : ""}
            {!run.matchedNothing && run.classified === 0 ? " Nothing in the register changed." : ""}
          </p>
        )}
        {run.classified > 0 && (
          <p className="ds-card__detail">
            Classifying fills in the cluster and category that facet assembly matches on, so whichever of
            these came back carrying them can now be pulled into a piece that has no keyword.
          </p>
        )}
        {/* What this run applied, facet by facet, in the order the brief
            ranks them. A run into the site's own vocabulary and one that
            invented its own are the difference between facets that group and
            facets that do not, so they must never read alike afterwards. A
            facet the server did not report is silent here rather than
            reported as empty. */}
        {facetState(cl) === "some" && (
          <>
            <p className="ds-card__detail">
              {did ? "The run could reuse" : "The run was given"} the {plural(cl.total, "cluster", "clusters")} the
              keywords here already carried, which is the field facet assembly asks for before the category.
              {did
                ? " The Cluster column below shows what each keyword ended up with."
                : " Whatever stopped this run, a missing cluster vocabulary was not it."}
              {shortBy(cl)}
            </p>
            <VocabNames v={cl} />
          </>
        )}
        {facetState(cl) === "none" && (
          <p className="ds-card__detail">
            {did
              ? "There was no existing cluster to reuse, so any cluster these keywords carry now is one this run made by grouping them. The Cluster column below shows what landed."
              : "There was no existing cluster to reuse, and nothing was classified, so the register still carries none."}
          </p>
        )}
        {facetState(cl) === "failed" && (
          <p className="ds-card__detail">Which clusters the run could reuse could not be read: {cl.error}</p>
        )}
        {facetState(cat) === "some" && (
          <>
            <p className="ds-card__detail">
              {did ? "Classified into" : "The run was given"} the {plural(cat.total, "category", "categories")} this
              site already uses{cat.sources.length ? ` (${cat.sources.join(", ")})` : ""}
              {did
                ? ", so these keywords share their buckets with everything else carrying them."
                : ", so whatever stopped this run, a missing vocabulary was not it."}
              {shortBy(cat)}
            </p>
            <VocabNames v={cat} />
          </>
        )}
        {facetState(cat) === "none" && (
          <p className="ds-card__detail">
            <b>No category vocabulary was applied.</b> {NO_VOCAB_WHY} {NO_VOCAB_FIX}
          </p>
        )}
        {facetState(cat) === "failed" && (
          <p className="ds-card__detail">Which categories the run classified into could not be read: {cat.error}</p>
        )}
        {facetState(tg) === "some" && (
          <p className="ds-card__detail ds-card__note">Tags: the {tg.total} this site already uses were there to be reused, and a run is free to add a granular one none of them covered.</p>
        )}
        {facetState(tg) === "none" && (
          <p className="ds-card__detail ds-card__note">Tags: this site had none to reuse, so every tag this run wrote is a new one.</p>
        )}
        {facetState(tg) === "failed" && (
          <p className="ds-card__detail ds-card__note">Tags: which ones the run could reuse could not be read ({tg.error}).</p>
        )}
        {/* Belonging, last, because it is the one finding that is not about
            how a keyword is filed. It renders only when the response carried
            it: a server that said nothing about fit says nothing here. */}
        <FitReport fit={run.fit} />
        {/* Dismiss lives on the strip now, where the report is thrown away
            from; everything that ACTS on the run stays here with the report
            that explains it. */}
        <div className="ds-card__foot">
          {left > 0 && <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={busy} onClick={() => onContinue(run.next)}>{busy ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />Classifying…</> : <><I name="sparkles" size={14} />Classify the next {Math.min(PER_RUN, left)}</>}</button>}
          {facetState(cat) === "none" && <TaxonomyLink onGo={onTaxonomy} />}
          {left > 0 && <span className="ds-card__note">{plural(left, "keyword is", "keywords are")} still without facets or a fit verdict, {PER_RUN} per run, and each run uses tokens</span>}
        </div>
      </ReportStrip>
    );
  }

  // ---- Coverage ----

  /** How many rows of a finding a card shows before it is expanded. */
  const CARD_SAMPLE = 6;

  // "showing 6 of 173". Every list the endpoint returns is capped at 60 with the
  // true total beside it, and a card shows fewer still, so what is on screen is
  // never allowed to read as the whole finding.
  function showingNote(shown, received, total) {
    if (total <= shown) return null;
    const tail = received < total ? `, and the scan returns ${received} at a time` : "";
    return <span className="ds-card__note">showing {shown} of {total}{tail}</span>;
  }

  // One published page. A page with a URL opens; one without is still shown,
  // because dropping it would understate the finding, but it is a plain chip
  // rather than a control that goes nowhere.
  function PageChip({ page }) {
    const href = page.post_url || page.url || "";
    const label = page.post_title || page.title || href || `post #${page.post_id}`;
    return href
      ? <a className="ds-chip" href={href} target="_blank" rel="noopener noreferrer" title={`Open ${href}`}>{label}</a>
      : <span className="ds-chip" title="The scan stored no URL for this page, so it cannot be opened from here.">{label}</span>;
  }

  // The scan's own milestones, read from GET /api/runs/:id. Nothing here is on a
  // timer: when the server stops reaching milestones the bar stops moving, which
  // is the whole point of reading progress instead of inventing it.
  function SyncProgress({ run }) {
    const failed = !!run && run.status === "failed";
    return (
      <div className="bi-fetch-progress">
        <div className="ml-progress-row" style={{ marginBottom: 10 }}>
          <div className="ml-progress"><div className="ml-progress__bar" style={{ width: (run ? run.pct : 0) + "%" }} /></div>
          <span className="ml-progress-row__pct">{run ? run.pct : 0}%</span>
        </div>
        <div className="bi-fetch-step bi-fetch-step--active">
          <span className="bi-fetch-step__dot">
            {failed ? <I name="alert-triangle" size={14} /> : <span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />}
          </span>
          <span className="bi-fetch-step__label">
            {run ? `Step ${Math.max(1, run.step)} of ${run.steps} · ${run.label || "working"}` : "Starting the scan"}
            {run && run.note ? <span style={{ color: "var(--text-muted)" }}> · {run.note}</span> : null}
          </span>
        </div>
      </div>
    );
  }

  /**
   * The coverage picture, over every published page the last scan stored.
   *
   * Three findings, ranked by how much damage they do. Cannibalisation leads:
   * two pages competing for one search is a live problem the site is already
   * paying for, while a keyword nothing targets is only an unused opportunity.
   *
   * A finding is actionable only where the screen can honestly act. A page opens
   * (the URL is real), an unattached keyword promotes to a plan (the row action
   * this screen already performs). Cannibalisation gets no bulk control, because
   * deciding which of two pages should win a search is not something this screen
   * can do; it is still shown, because a finding with no action is worth knowing.
   */
  function CoveragePanel({ cov, open, setOpen, panelRef, onRetry, onSync, syncing, run, onPromote, promoting, onSelect }) {
    const [expand, setExpand] = useState({});
    const toggle = (id) => setExpand(e => ({ ...e, [id]: !e[id] }));

    // A scan in flight replaces the picture rather than sitting beside it: every
    // number below is about to be rewritten, so showing them next to a running
    // scan would be showing the previous answer as if it were the current one.
    if (syncing) return <div className="ds-strip" ref={panelRef}><SyncProgress run={run} /></div>;
    if (cov.loading && !cov.data) {
      return <div className="ds-strip ds-strip--quiet"><I name="loader" size={14} />Reading the coverage picture...</div>;
    }
    // A read that failed is not a site with no gaps, so it never renders as one.
    if (cov.error) {
      return (
        <div className="ds-strip ds-strip--error">
          <I name="alert-triangle" size={14} />
          <span>Could not read the coverage picture: {cov.error}</span>
          <button className="bi-btn bi-btn--sm" onClick={onRetry}>Try again</button>
        </div>
      );
    }
    const d = cov.data;
    if (!d) return null;

    // Nobody has scanned this site, so there is no coverage data. That is a
    // different answer from "scanned, no gaps found", and it must not look like
    // a clean bill of health.
    if (d.never_scanned) {
      return (
        <div className="ds-strip" ref={panelRef}>
          <div className="ds-cards">
            <div className="ds-card ds-card--todo">
              <div className="ds-card__head">
                <span className="ds-card__icon"><I name="alert-triangle" size={15} /></span>
                <span className="ds-card__title">No coverage picture for this site yet</span>
              </div>
              <p className="ds-card__detail">
                Nothing has read this site, so nothing here knows which pages it publishes, which of them
                declare a focus keyword, or which keywords two pages are competing for. This is an absence
                of data, not a clean bill of health.
              </p>
              <div className="ds-card__foot">
                <button className="bi-btn bi-btn--sm" onClick={onSync}>Scan the site</button>
                <span className="ds-card__note">reads every published post and page over the REST API</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const bare  = d.pages_without_keyword || [];
    const cann  = d.cannibalised || [];
    const loose = d.keywords_unattached || [];
    const bareN  = d.pages_without_keyword_total || 0;
    const cannN  = d.cannibalised_total || 0;
    const looseN = d.keywords_unattached_total || 0;
    const when = d.last_scanned_at ? `scanned ${ago(d.last_scanned_at)}` : "scan date unknown";

    // Scanned, and genuinely nothing to report. Said in one line rather than
    // rendered as empty space, which on a screen that sometimes shows cards is
    // indistinguishable from a panel that failed to load.
    if (!bareN && !cannN && !looseN) {
      return (
        <div className="ds-strip ds-strip--quiet" ref={panelRef}>
          <I name="check" size={14} />
          Every one of the {d.pages_total} published pages declares a focus keyword, every keyword has a page,
          and no two pages compete for one. Last {when}.
        </div>
      );
    }

    const cards = [];

    if (cannN) {
      const shown = expand.cann ? cann : cann.slice(0, CARD_SAMPLE);
      cards.push(
        <DetailLine key="cann" tone="problem" title={`${plural(cannN, "keyword", "keywords")} more than one page targets`}
          subtitle="Pages competing for the same search">
          <p className="ds-card__detail">
            Those pages compete with each other for the same search, so neither wins it cleanly. Usually one
            has to absorb the other or change what it targets. Open them to compare.
          </p>
          {shown.map(c => (
            <div className="ds-card__sample" key={c.ref_id}>
              <span className="research-chip"><b>{c.keyword || `keyword #${c.ref_id}`}</b></span>
              {(c.posts || []).map(p => <PageChip key={p.post_id} page={p} />)}
            </div>
          ))}
          <div className="ds-card__foot">
            {cann.length > CARD_SAMPLE && <button className="bi-btn bi-btn--sm" onClick={() => toggle("cann")}>{expand.cann ? "Show fewer" : `Show all ${cann.length}`}</button>}
            {showingNote(shown.length, cann.length, cannN)}
          </div>
        </DetailLine>
      );
    }

    if (bareN) {
      const shown = expand.bare ? bare : bare.slice(0, CARD_SAMPLE);
      cards.push(
        <DetailLine key="bare" tone="todo" title={`${plural(bareN, "published page", "published pages")} with no focus keyword`}
          subtitle={`Out of ${d.pages_total} the scan read`}>
          <p className="ds-card__detail">
            Out of {d.pages_total} the scan read. Nothing declares what these pages are meant to rank for, so
            nothing here can tell you whether they are working. Open one to see what it is about.
          </p>
          <div className="ds-card__sample">{shown.map(p => <PageChip key={p.post_id} page={p} />)}</div>
          <div className="ds-card__foot">
            {bare.length > CARD_SAMPLE && <button className="bi-btn bi-btn--sm" onClick={() => toggle("bare")}>{expand.bare ? "Show fewer" : `Show all ${bare.length}`}</button>}
            {showingNote(shown.length, bare.length, bareN)}
          </div>
        </DetailLine>
      );
    }

    if (looseN) {
      const shown = expand.loose ? loose : loose.slice(0, CARD_SAMPLE);
      cards.push(
        <DetailLine key="loose" tone="opportunity" title={`${plural(looseN, "keyword", "keywords")} no page targets`}
          subtitle="In the register, with nothing published aimed at them"
          action={<button className="bi-btn bi-btn--sm" onClick={() => onSelect(loose.map(k => k.id))}>Select all {loose.length}</button>}>
          <p className="ds-card__detail">
            In the register, but nothing published is aimed at them. Promote one straight to a content plan,
            or select them in the table to decide how the work splits.
          </p>
          <div className="research-cap-row">
            {shown.map(k => (
              <button className="research-cap-btn" key={k.id} disabled={promoting != null} title={`Promote "${k.keyword}" to a content plan`} onClick={() => onPromote({ id: k.id, kw: k.keyword })}>
                {promoting === k.id ? <span className="ml-spinner ml-spinner--sm" style={{ width: 12, height: 12 }} /> : <I name="arrow-right" size={12} />}{k.keyword}
              </button>
            ))}
          </div>
          <div className="ds-card__foot">
            {/* Acts on every id this scan returned, not only the ones on screen,
                so the note below says which number that is. It no longer claims
                they land "in the table": the table pages, so some of them will
                be on a later one, and the selection holds them all either way
                because the bulk actions act on ids. */}
            <button className="bi-btn bi-btn--sm" onClick={() => onSelect(loose.map(k => k.id))}>Select all {loose.length}</button>
            {loose.length > CARD_SAMPLE && <button className="bi-btn bi-btn--sm" onClick={() => toggle("loose")}>{expand.loose ? "Show fewer" : `Show all ${loose.length}`}</button>}
            {showingNote(shown.length, loose.length, looseN)}
          </div>
        </DetailLine>
      );
    }

    return (
      <div className="ds-strip" ref={panelRef}>
        <button className="ds-strip__head" aria-expanded={open} onClick={() => setOpen(o => !o)}>
          <I name={open ? "chevron-down" : "chevron-right"} size={15} />
          <span className="ds-strip__title">Coverage across {plural(d.pages_total, "published page", "published pages")}</span>
          <span className="ds-strip__sub">{when} · {d.keywords_attached} of {d.keywords_total} keywords have a page</span>
        </button>
        {open && <div className="ds-lines">{cards}</div>}
      </div>
    );
  }

  // ---- Main table ----

  /**
   * How many rows of the register the table holds at once.
   *
   * The register was rendered whole. On the live site that is 1,991 rows, about
   * 26,000 elements and 8,000 event-bound controls in one table, which React
   * re-ran on every keystroke in the search box and every checkbox: the browser
   * stopped responding. Fifty is a page somebody can read.
   *
   * The rows are the only thing this cuts. Every COUNT on this screen is
   * answered by GET /api/keywords/summary over the whole filtered register,
   * because a page size that quietly turned "1,991 keywords" into "50" would be
   * the silent truncation this codebase keeps having to undo, and it would be
   * worse than the slowness it was fixing.
   */
  const PAGE_SIZE = 50;

  /** A count nothing could read is not a zero, and never renders as one. */
  const num = (n) => (typeof n === "number" ? n.toLocaleString() : "?");

  /* The register seen by cluster: one line per AI group.

     Four things this view had to be told, each the hard way:

     - A line OPENS, it does not navigate. Clicking a cluster used to jump to
       the filtered table, so the one gesture a list invites cost the reader
       the page they were on. The click opens a drawer about that cluster;
       going to its keywords is a button inside it.
     - Its actions must be reachable. Buttons packed into a table cell under a
       row-level click handler read as dead controls, because the row's own
       navigation answered first.
     - It must act on MANY. The keywords tab has a select mode and a bulk bar,
       and a cluster list without one makes a user plan thirty groups one
       press at a time.
     - It must sit where the keywords tab sits. `.bi-page` centres at 1200px
       and only the keyword table escapes that (research.css lifts the cap for
       `.kw-table-wrap`), so this page says so for itself. */
  /* `marketId` for the same reason the table takes it: the clusters are a
   * second READING of one register, so a cluster list answering for the whole
   * website while the table beside it answers for one market would be two
   * screens disagreeing about what the register holds. */
  function ClustersView({ site, onKeywords, go, marketId }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const DL = window.MlReportDrawer && window.MlReportDrawer.DetailLine;
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [tick, setTick] = useState(0);
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const [busy, setBusy] = useState(null);     // a cluster name, or "__bulk__"
    const [note, setNote] = useState(null);     // { ok, text }
    useEffect(() => {
      if (siteId == null) { setState({ loading: false, error: "No active site.", data: null }); return; }
      let cancel = false;
      setState(s => ({ ...s, loading: true, error: null }));
      api(`/keywords/clusters?${marketParam(new URLSearchParams({ site_id: String(siteId) }), marketId).toString()}`)
        .then(d => { if (!cancel) setState({ loading: false, error: null, data: d }); })
        .catch(e => { if (!cancel) setState({ loading: false, error: e.message || "The clusters could not be read.", data: null }); });
      return () => { cancel = true; };
    }, [siteId, tick, marketId]);

    const d = state.data;
    const clusters = (d && d.clusters) || [];
    const none = (d && d.unclustered) || { count: 0, volume: 0 };
    const toggle = (name) => setSel(s => s.includes(name) ? s.filter(x => x !== name) : [...s, name]);

    /* One cluster to one plan: the group IS the body of work, and its whole
       keyword set lands in the plan's unclaimed pool through the additive
       write, so nothing a piece already claims is disturbed. It returns the
       outcome instead of rendering it, so the single and the bulk path report
       in the same words. */
    const planOne = async (name) => {
      const idRows = await api(`/keywords?site_id=${siteId}&cluster=${encodeURIComponent(name)}&fields=id`);
      const ids = (Array.isArray(idRows) ? idRows : []).map(r => r.id).filter(Boolean);
      if (!ids.length) throw new Error(`"${name}" has no readable keywords.`);
      const plan = await api(`/content-plans`, { method: "POST", body: JSON.stringify({ site_id: siteId, title: name }) });
      if (!plan || plan.id == null) throw new Error(`The plan for "${name}" was created but the server did not return it.`);
      const put = await api(`/content-plans/${plan.id}/ingredients`, { method: "POST", body: JSON.stringify({ site_id: siteId, ingredients: ids.map(id => ({ kind: "keyword", ref_id: id })) }) });
      return { planId: plan.id, added: put.added || 0 };
    };

    const planCluster = async (name) => {
      if (siteId == null || busy) return;
      setBusy(name); setNote(null);
      try {
        const r = await planOne(name);
        setNote({ ok: true, text: `Plan "${name}" created with ${num(r.added)} keyword${r.added === 1 ? "" : "s"} in its pool. Open it and press Propose pieces to turn the group into articles.` });
      } catch (e) { setNote({ ok: false, text: e.message || "Could not plan this cluster." }); }
      finally { setBusy(null); }
    };

    /* Many clusters, one plan each, caught PER cluster: a group that fails
       must not abandon the rest, and the outcome names all three numbers
       rather than reporting what worked as the whole run. */
    const planSelected = async () => {
      if (siteId == null || busy || !sel.length) return;
      setBusy("__bulk__"); setNote(null);
      let ok = 0, keywords = 0; const failed = [];
      for (const name of sel) {
        try { const r = await planOne(name); ok += 1; keywords += r.added; }
        catch { failed.push(name); }
      }
      const parts = [`${ok} plan${ok === 1 ? "" : "s"} created`, `${num(keywords)} keyword${keywords === 1 ? "" : "s"} attached`];
      if (failed.length) parts.push(`${failed.length} failed (${failed.slice(0, 3).join(", ")}${failed.length > 3 ? ", and more" : ""})`);
      setNote({ ok: failed.length === 0, text: parts.join(", ") + ". Open the Content Planner and press Propose pieces on each." });
      setSel(failed);
      if (!failed.length) setSelectMode(false);
      setBusy(null);
    };

    const line = (name, count, volume, inUse, isNone) => {
      const title = `${name} · ${num(count)} keyword${count === 1 ? "" : "s"}${volume ? ` · ${num(volume)} volume` : ""}`;
      const body = (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", color: "var(--text-primary)" }}>
            <span><b>{num(count)}</b> keywords</span>
            <span><b>{num(volume)}</b> total volume</span>
            {!isNone && <span><b>{num(inUse || 0)}</b> already in a plan</span>}
          </div>
          <p style={{ margin: 0 }}>
            {isNone
              ? "These keywords carry no cluster yet, so facet assembly has nothing to group them by. A Classify run (or Re-analyze all) writes the first clusters."
              : "A cluster is the set of keywords one page should serve. Planning it creates a content plan carrying the whole group, ready for Propose pieces to turn into articles."}
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="bi-btn bi-btn--sm" onClick={() => onKeywords(isNone ? "__none__" : name)}><I name="target" size={14} />View its keywords</button>
            {!isNone && <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={!!busy} onClick={() => planCluster(name)}>{busy === name ? "Planning..." : "Plan this cluster"}</button>}
          </div>
        </div>
      );
      const action = isNone
        ? <button className="bi-btn bi-btn--sm" onClick={() => onKeywords("__none__")}><I name="target" size={14} />View keywords</button>
        : <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={!!busy} onClick={() => planCluster(name)}>{busy === name ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 13, height: 13 }} />Planning...</> : <><I name="shuffle" size={14} />Plan</>}</button>;
      const key = isNone ? "__none__" : name;
      const row = DL
        ? <DL key={key} tone={isNone ? "todo" : "opportunity"} title={title} subtitle={isNone ? "Not clustered yet" : "Keyword cluster"} action={action}>{body}</DL>
        : <div key={key} className="ds-line"><span className="ds-line__title">{title}</span>{action}</div>;
      // The unclustered set is deliberately not selectable: it is not a group,
      // so planning it would mean planning everything the classifier has not
      // judged yet.
      if (!selectMode || isNone) return row;
      const on = sel.includes(name);
      return (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label className="hds-check" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={on} onChange={() => toggle(name)} /><span className="hds-check__box"><I name="check" size={12} /></span>
          </label>
          <div style={{ flex: 1, minWidth: 0 }}>{row}</div>
        </div>
      );
    };

    return (
      <div className="bi-page" style={{ maxWidth: "none" }}>
        <div className="bi-header">
          <div className="bi-header__titles"><h1 className="bi-title"><span className="bi-title__icon"><I name="layers" size={20} /></span>Keywords Planner</h1></div>
          <div className="bi-header-actions">
            {clusters.length > 0 && <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>}
            <button className="bi-btn" onClick={() => setTick(t => t + 1)}><I name="refresh" size={16} />Refresh</button>
          </div>
        </div>
        <p className="research-intro">The register seen by cluster: the AI groups a Classify run writes, each the set of keywords one page should serve. Open a line for its details, or select several and plan them together.</p>
        <div className="kw-seg" style={{ marginBottom: 12 }}>
          <button onClick={() => onKeywords(undefined)}>Keywords</button>
          <button className="on">Clusters</button>
        </div>

        {note && (
          <div className={"ds-strip " + (note.ok ? "ds-strip--quiet" : "ds-strip--error")} style={{ marginBottom: 10 }}>
            <I name={note.ok ? "check-circle" : "alert-triangle"} size={14} /><span style={{ flex: 1 }}>{note.text}</span>
            {go && <button className="bi-btn bi-btn--sm" onClick={() => go("content-planner")}><I name="arrow-up-right" size={13} />Open Content Planner</button>}
            <button className="bi-btn bi-btn--sm" onClick={() => setNote(null)}>Dismiss</button>
          </div>
        )}

        {selectMode && (
          <div className="bi-bulk-bar">
            <span>{sel.length ? `${sel.length} cluster${sel.length === 1 ? "" : "s"} selected` : "Nothing selected"}</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel(clusters.map(c => c.name))}>Select all {clusters.length}</button>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
            <div style={{ flex: 1 }} />
            <button className="bi-btn bi-btn--sm bi-btn--primary" disabled={!sel.length || !!busy} onClick={planSelected}>
              {busy === "__bulk__" ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 13, height: 13 }} />Planning {sel.length}...</> : <><I name="shuffle" size={14} />Plan {sel.length || ""} as {sel.length === 1 ? "a plan" : "plans"}</>}
            </button>
          </div>
        )}

        {state.loading ? <div className="bi-subempty">Reading the clusters...</div>
          : state.error ? (
            <div className="ds-strip ds-strip--error"><I name="alert-triangle" size={14} /><span>{state.error}</span><button className="bi-btn bi-btn--sm" onClick={() => setTick(t => t + 1)}>Retry</button></div>
          ) : clusters.length === 0 && none.count === 0 ? (
            <div className="bi-subempty">No keywords in the register yet. Import or capture some first.</div>
          ) : (
            <div className="ds-lines">
              {none.count > 0 && line("No cluster yet", none.count, none.volume, 0, true)}
              {clusters.map(c => line(c.name, c.count, c.volume, c.in_use, false))}
            </div>
          )}

        {d && clusters.length === 0 && none.count > 0 && (
          <div className="bi-hint" style={{ marginTop: 10 }}>No clusters yet: a Classify run writes them. Press Re-analyze all on the Keywords tab and this view fills in.</div>
        )}
      </div>
    );
  }


  /* ── THE THIRD READING OF ONE REGISTER: where pieces overlap ──────────────
   *
   * Inside this screen and not behind a sidebar entry of its own: a new KIND of
   * an existing thing belongs in that thing's existing selector, and two entries
   * for one subject is how two screens come to disagree about what the website
   * is. The segmented control already offers Keywords and Clusters; this is the
   * third reading.
   *
   * WHAT IT SAYS, and every word of it is load bearing. Two pieces targeting
   * one term in the SAME LANGUAGE is overlapping intent: Google will pick one of
   * them to answer the query and largely ignore the other, and the pick can
   * change week to week. There is NO PENALTY, and the screen never calls it one,
   * never calls it an error, and never offers deleting the keyword, because the
   * keyword is not the problem.
   *
   * A FRENCH PIECE AND ITS ENGLISH COUNTERPART ARE NOT A FINDING. They are
   * hreflang alternates serving different audiences. They are shown, under
   * their own heading, so the reader can see the rule was applied rather than
   * wonder whether it was.
   *
   * AND IT IS NOT OBSERVED. The strongest evidence of cannibalisation is two of
   * your pages taking impressions for one query, and the stored Search Console
   * data cannot show it: the page and query dimensions are stored separately,
   * with no row joining one to the other. The limit is printed at the top of the
   * view in the server's own words, never softened.
   *
   * Every sentence here comes from the response (`wording`), which comes from
   * constants in backend/lib/cannibalization.js, so the screen and the API
   * cannot drift into describing one finding two ways. */
  function OverlapView({ onBack, site, sectionId, atAll, marketId }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const DL = window.MlReportDrawer && window.MlReportDrawer.DetailLine;
    const [state, setState] = useState({ loading: true, error: null, data: null });
    useEffect(() => {
      if (siteId == null) { setState({ loading: false, error: "No active site.", data: null }); return; }
      let cancel = false;
      setState(s => ({ ...s, loading: true, error: null }));
      const p = marketParam(positionParam(new URLSearchParams({ site_id: String(siteId) }), sectionId), marketId);
      api(`/keywords/overlap?${p.toString()}`)
        .then(x => { if (!cancel) setState({ loading: false, error: null, data: x }); })
        .catch(e => { if (!cancel) setState({ loading: false, error: e.message || "The overlap could not be read.", data: null }); });
      return () => { cancel = true; };
    }, [siteId, sectionId, marketId]);

    const d = state.data;
    const w = (d && d.wording) || {};
    const sections = (d && Array.isArray(d.sections)) ? d.sections : null;
    const sectionName = (id) => {
      const hit = sections && sections.find(x => Number(x.site_id) === Number(id));
      return hit ? (hit.name || hit.url || `#${id}`) : `#${id}`;
    };
    // The position the server ANSWERED at, never the one that was asked for.
    const wide = !!(d && d.scope && d.scope.position === "market");
    const reg = (d && d.register) || {};

    /* One piece, named well enough to act on: its title, the plan it belongs
       to, the section that will publish it, and how it names the term. */
    const piece = (p) => (
      <li key={p.item_id}>
        <b>{p.title || `Piece ${p.item_id}`}</b>
        <span className="ds-card__note" style={{ marginLeft: 8 }}>
          {p.plan_title ? `in "${p.plan_title}"` : `in plan ${p.plan_id}`}
          {p.site_id != null ? ` · ${sectionName(p.site_id)}` : ""}
          {(p.evidence || []).some(e => e.how === "primary_keyword") ? " · its primary keyword" : " · a claimed keyword"}
        </span>
      </li>
    );

    /* A finding as a LINE that opens, which is the house pattern for a block of
       explanation: the title is the whole finding, and the cost, the remedies
       and the pieces live in the drawer it opens. */
    const line = (f, { language, note }) => {
      const title = language
        ? `"${f.term}" (${langLabel(language) || language}) is targeted by ${plural(f.pieces.length, "piece", "pieces")}`
        : `"${f.term}" is targeted by ${plural(f.pieces.length, "piece", "pieces")}, and no language is recorded`;
      const body = (
        <div className="ds-brief">
          <div>{language ? w.cost : note}</div>
          {language && <div>
            <b>What to do</b>
            <ul>{(w.remedies || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
            <div className="ds-card__note">{w.not_a_remedy}</div>
          </div>}
          <div>
            <b>The pieces</b>
            <ul>{f.pieces.map(piece)}</ul>
          </div>
        </div>
      );
      return DL
        ? <DL key={`${f.term_key}-${language || "none"}`} title={title} tone={language ? "warning" : "quiet"}>{body}</DL>
        : <div key={`${f.term_key}-${language || "none"}`} className="ds-card"><b>{title}</b>{body}</div>;
    };

    return (
      <div className="bi-page" style={{ maxWidth: 1200 }}>
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to Keywords Planner</button>
        <div className="bi-header">
          <div className="bi-header__titles">
            <h1 className="bi-title"><span className="bi-title__icon"><I name="target" size={20} /></span>Where pieces overlap</h1>
          </div>
        </div>

        {/* THE LIMIT, FIRST, in the server's own words. It is at the top rather
            than in a footnote because a reader who takes this for Search
            Console evidence has misread the whole view. */}
        <div className="research-cost-note" style={{ marginTop: 0 }}>
          <I name="info" size={14} style={{ color: "var(--text-muted)" }} />
          {w.computed_from || "This is computed from what you planned."}
        </div>

        {state.loading && <div className="bi-subempty">Reading what your pieces target…</div>}
        {state.error && (
          <div className="ds-strip ds-strip--error">
            <I name="alert-triangle" size={14} />
            <span>{state.error}</span>
          </div>
        )}

        {d && (
          <>
            {/* WHICH SCOPE ANSWERED. Overlap between two sections is only
                visible at the whole website, so a reader at one section has to
                know that is what they are looking at. */}
            <div className="ds-strip ds-strip--quiet">
              <I name={wide ? "layers" : "globe"} size={14} />
              <span>
                {wide
                  ? <>Across <b>the whole website</b>: one market shares one keyword register, so two pieces on two sections competing for one term are visible here and nowhere else.</>
                  : <>This section only. Two pieces on <i>different</i> sections targeting one term cannot be seen from here: move the switcher to "The whole website".</>}
                {atAll && !wide && d.scope && d.scope.why ? <> {d.scope.why}</> : null}
              </span>
            </div>

            {/* THE REGISTER'S OWN SHAPE, which is what tells an empty findings
                list apart from a clean one. On a register where nothing has ever
                recorded a language, "no overlap found" would be a lie of
                omission. */}
            <div className="ds-strip ds-strip--quiet">
              <I name="info" size={14} />
              <span>
                {num(reg.total)} {reg.total === 1 ? "keyword" : "keywords"} in scope.
                {reg.no_language ? ` ${num(reg.no_language)} of them carry no language, so any overlap among those is reported as unknown rather than as a finding.` : ""}
                {reg.languages ? ` ${plural(reg.languages, "language is", "languages are")} recorded.` : " No language is recorded on any of them."}
              </span>
            </div>

            {d.limits && d.limits.capped && (
              <div className="ds-strip ds-strip--warning">
                <I name="alert-triangle" size={14} />
                <span>{d.limits.why}</span>
              </div>
            )}

            {/* The findings. The count is over everything read and the list is
                sliced from it, so a list shorter than its count says so. */}
            <h2 className="bi-section-title">
              Overlapping intent
              <span className="ds-card__note" style={{ marginLeft: 8 }}>
                {num(d.findings_total)}
                {d.findings_total > d.findings.length ? ` · showing the first ${d.findings.length}` : ""}
              </span>
            </h2>
            {d.findings.length === 0
              ? <div className="bi-subempty">
                  No two pieces target the same term in the same recorded language
                  {reg.no_language ? ". That is not the same as a clean register: see the unknown-language list below." : "."}
                </div>
              : d.findings.map(f => line(f, { language: f.language }))}

            {/* UNKNOWN LANGUAGE. Its own list, never folded into the findings:
                a row whose language nothing recorded cannot be told apart from a
                translation pair, and guessing is the one thing this may not
                do. */}
            <h2 className="bi-section-title">
              Language not recorded
              <span className="ds-card__note" style={{ marginLeft: 8 }}>
                {num(d.unknown_language_total)}
                {d.unknown_language_total > d.unknown_language.length ? ` · showing the first ${d.unknown_language.length}` : ""}
              </span>
            </h2>
            <div className="research-cost-note" style={{ marginTop: 0 }}>
              <I name="info" size={14} style={{ color: "var(--text-muted)" }} />
              {w.unknown_language}
            </div>
            {d.unknown_language.length === 0
              ? <div className="bi-subempty">Every term targeted twice here carries a language.</div>
              : d.unknown_language.map(f => line(f, { language: null, note: w.unknown_language }))}

            {/* ALTERNATES. Shown so the rule can be SEEN to have been applied.
                A reader who knows their French and English blogs both target
                "scrum master" should find it HERE, and find out why it is not a
                problem, rather than find nothing and wonder. */}
            <h2 className="bi-section-title">
              Translation pairs, which are not overlap
              <span className="ds-card__note" style={{ marginLeft: 8 }}>{num(d.alternates_total)}</span>
            </h2>
            <div className="research-cost-note" style={{ marginTop: 0 }}>
              <I name="check" size={14} style={{ color: "var(--success)" }} />
              {w.alternates}
            </div>
            {d.alternates.length === 0
              ? <div className="bi-subempty">No term here is targeted in more than one recorded language.</div>
              : <div className="kw-table-wrap">
                  <table className="kw-table">
                    <thead><tr><th>Term</th><th>Languages</th><th>Pieces</th></tr></thead>
                    <tbody>
                      {d.alternates.map(a => (
                        <tr key={a.term_key}>
                          <td className="kw-kw">{a.term}</td>
                          <td>{a.languages.map(l => <span key={l.language} className="cluster-tag" style={{ marginRight: 6 }} title={`Stored language: ${l.language}`}>{langLabel(l.language) || l.language}</span>)}</td>
                          <td className="kw-num">{a.languages.map(l => `${l.piece_count} in ${langLabel(l.language) || l.language}`).join(" · ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
          </>
        )}
      </div>
    );
  }

  /* `sectionId` is the SWITCHER'S OWN FILTER, and the keyword register is per
   * MARKET (owner, 2026-10-20), so this screen has a position:
   *
   *   undefined   this screen was not told. Every request goes out exactly as it
   *               did before this change, and the screen shows what it has
   *               always shown. NOT the same as being told it is at All.
   *   null        the All position, "the whole website": the market's whole
   *               register, every section's rows, each row naming its section.
   *   a number    one section, which is byte for byte today's answer.
   *
   * WHY THE UNDEFINED CASE EXISTS AT ALL rather than defaulting to All. `site`
   * is the ANCHOR connection and authorises the request either way; the
   * position is a separate fact, and a screen that assumed All when nobody told
   * it would widen a register under a customer who had selected one section. */
  /* `markets`, `marketId` and `onPickMarket` are the MARKET CONTROL, and the
   * three of them are the shell's: a market is a fact about the website, so the
   * selection lives where the switcher lives and not in this screen
   * (client/src/main.jsx has the reasoning beside `propertyMarkets`).
   *
   * `markets` undefined or shorter than two is the normal case and draws
   * nothing, so a one market website reads exactly as it did. `marketId` is
   * then null and no market_id is sent, which is what makes this additive. */
  function KeywordsPlanner({ onWrite, site, go, sectionId, markets, marketId, onPickMarket }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    // The All position, explicitly. `sectionId === null` and nothing looser:
    // undefined is "not told" and must never read as All.
    const atAll = sectionId === null;
    const [view, setView] = useState("table"); // table | import | manual | clusters | overlap
    const [status, setStatus] = useState("all");
    const [usage, setUsage] = useState("all");
    // Off-market and off-topic rows are hidden by DEFAULT, because a register
    // whose imported half belongs to somebody else's business is unreadable
    // otherwise. Hidden, never deleted, and the strip below the toolbar says how
    // many are hidden: a screen quietly withholding 1,400 rows is worse than one
    // showing them.
    const [fitView, setFitView] = useState("keep");
    // The facet filters. Server-applied through filterQS like everything else,
    // so the bulk actions' "all N matching" and the summary count follow them.
    const [clusterF, setClusterF] = useState("all");
    const [formatF, setFormatF] = useState("all");
    const [categoryF, setCategoryF] = useState("all");
    const [search, setSearch] = useState("");
    // What the SERVER is searching for, which lags the box by a pause. A
    // LIKE '%term%' cannot use an index and scans the site's rows, so one scan
    // per pause is affordable where one per keystroke is not.
    const [q, setQ] = useState("");
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const [confirm, setConfirm] = useState(null);
    const [kws, setKws] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    // A page swap is not a load: the rows on screen stay where they are and dim,
    // because blanking the table on every Next reads as the register emptying.
    const [paging, setPaging] = useState(false);
    const [bulkStat, setBulkStat] = useState("pending");
    // A bulk act is now ONE request over a set the server resolves, so it can be
    // a set of 1,991. It is not instant, and pressing Delete twice on a register
    // that size is not something a person should be able to do by accident.
    const [bulkBusy, setBulkBusy] = useState(false);
    const [promoting, setPromoting] = useState(null);   // keyword id mid-POST
    const [gscBusy, setGscBusy] = useState(false);
    const [classifying, setClassifying] = useState(false);
    const [runReport, setRunReport] = useState(null);   // the last classification run, in full
    // Whether the next classify run also re-clusters rows that already carry
    // facets. It lives beside the dialog that asks, and it is reset every time
    // that dialog opens: spending tokens twice on the same rows is a choice
    // made per run, never a preference this screen remembers.
    const [confirmRejudge, setConfirmRejudge] = useState(false);
    // A report is about one site's register, so switching sites drops it rather
    // than leaving numbers on screen that describe somewhere else. The rows go
    // with it: they are another site's, and holding them while the new ones load
    // would show one site's keywords under another site's counts.
    // And the same when the POSITION moves: the rows on screen belong to the
    // previous position, and holding them while the new ones load would show one
    // scope's keywords under another scope's counts.
    useEffect(() => { setRunReport(null); setKws([]); setLoading(true); }, [siteId, sectionId, marketId]);
    const [invKey, setInvKey] = useState(0);            // bumped on every write, so the strip recounts
    const [toast, setToast] = useState(null);           // { msg, bad }
    const toastT = useRef(null);
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    // Coverage: the site-wide picture, and the scan that fills it.
    const [cov, setCov] = useState({ loading: true, error: null, data: null });
    const [covOpen, setCovOpen] = useState(true);
    const [covSyncing, setCovSyncing] = useState(false);
    const [covRun, setCovRun] = useState(null);         // the server's own milestones
    const [covErr, setCovErr] = useState(null);         // a failed scan, kept on screen
    const covStop = useRef(null);
    const covRef = useRef(null);
    useEffect(() => () => { if (typeof covStop.current === "function") covStop.current(); }, []);

    // This module has no toast host, so it keeps a local auto-clearing one. Both
    // the success and the failure path go through it: a write that did not land
    // must say so rather than fall silent like the old no-op buttons did.
    const showToast = (msg, bad) => {
      setToast({ msg, bad: !!bad });
      if (toastT.current) clearTimeout(toastT.current);
      toastT.current = setTimeout(() => setToast(null), 3600);
    };
    useEffect(() => () => { if (toastT.current) clearTimeout(toastT.current); }, []);

    // The route to the vocabulary maker, and null when this screen was mounted
    // without a navigator. Null is passed down as it is: what depends on it
    // renders the destination in words rather than a button that would do
    // nothing.
    const goTaxonomy = go ? () => go(TAXONOMY_SCREEN) : null;

    /* WHAT CANNOT BE DONE FROM THE ALL POSITION, and why it is disabled rather
     * than quietly acting on the anchor.
     *
     * The register READS at the market. These acts WRITE, or spend tokens, for
     * ONE CONNECTION: a classify run, a bulk status change, a bulk delete, an
     * import, a coverage scan, a Search Console pull. Running one from All would
     * either act on the anchor while the screen showed the whole website (the
     * quietly wrong set) or widen a delete across connections on a count taken
     * somewhere else. A per ROW act is fine and stays enabled: it is addressed
     * to the section that holds the row.
     *
     * Disabled WITH THE REASON on the control, and the reason names the way
     * out, which is the switcher this position came from. */
    const perSectionOnly = atAll
      ? "This runs on one section at a time. Pick a section in the switcher at the top."
      : undefined;
    const blocked = atAll;

    // The search box, debounced. Nothing else here debounces because nothing
    // else here is a table scan.
    useEffect(() => {
      const t = setTimeout(() => setQ(search.trim()), 300);
      return () => clearTimeout(t);
    }, [search]);

    /* THE FILTERS ARE THE SERVER'S, and this is the one place they are written.
     *
     * A page of a client-side-filtered list means nothing: page 2 of "the fifty
     * rows I happen to hold, minus the ones I hid" is not page 2 of anything.
     * So status, fit, search and priority travel to the query, and a page is a
     * page OF THE FILTERED SET whose total the summary states.
     *
     * The list and the summary compose the same string for exactly that reason:
     * a count answered over one set and rows drawn from another is how a screen
     * ends up disagreeing with itself. */
    const filterQS = useCallback(() => {
      const p = marketParam(positionParam(new URLSearchParams({ site_id: String(siteId) }), sectionId), marketId);
      if (status !== "all") p.set("status", KW_STATUS_OUT[status] || "raw");
      // `keep` and `unjudged` are answers the route knows by name, so the
      // default view (hiding off market and off topic) is one the server applies
      // rather than one this screen re-implements over rows it was sent.
      if (fitView !== "all") p.set("fit", fitView);
      if (q) p.set("search", q);
      if (usage === "high") p.set("priority", "high");
      if (clusterF !== "all") p.set("cluster", clusterF);
      if (formatF !== "all") p.set("format", formatF);
      if (categoryF !== "all") p.set("category", categoryF);
      return p;
    }, [siteId, sectionId, marketId, status, fitView, q, usage, clusterF, formatF, categoryF]);

    // A filter changes what the set IS, so page 1 is the only page that still
    // exists. Staying on page 7 of a set that now holds 30 rows shows nothing
    // and looks like an empty register.
    // Moving the switcher changes what the set IS, exactly as a filter does.
    // Changing the MARKET changes what the set is in the same way, so the same
    // reset applies: page 7 of France is not page 7 of the United States.
    useEffect(() => { setPage(1); }, [siteId, sectionId, marketId, status, fitView, q, usage, clusterF, formatF, categoryF]);

    /* Every id the filtered set holds, for the things a page cannot answer.
     *
     * Bulk operations take explicit ids, and "classify everything still
     * unclassified" is a claim about the register, not about the fifty rows on
     * screen. Ids are the cheap half of a row: the 1,991 that made the browser
     * stop responding as table rows are a few kilobytes as numbers, so asking
     * for them on demand costs nothing the table was paying. */
    /* DELIBERATELY WITHOUT THE POSITION. These ids feed acts that run per
     * connection (a classify run, a bulk status change, a bulk delete), and a
     * set of ids wider than the act that will consume them is the one mistake
     * here that cannot be taken back. The acts themselves are unavailable at
     * All and say so, so this never needs the wider answer. */
    const fetchIds = useCallback(async (extra) => {
      if (siteId == null) return [];
      const p = new URLSearchParams({ site_id: String(siteId), fields: "id", ...(extra || {}) });
      const rows = await api(`/keywords?${p.toString()}`);
      return (Array.isArray(rows) ? rows : []).map(r => Number(r.id)).filter(Boolean);
    }, [siteId]);

    const load = useCallback(async () => {
      if (siteId == null) { setKws([]); setLoading(false); return; }
      // Deliberately not setLoading(true): the rows already on screen are the
      // previous page and they stay until the next one lands.
      setPaging(true);
      const p = filterQS();
      p.set("limit", String(PAGE_SIZE));
      p.set("offset", String((page - 1) * PAGE_SIZE));
      try { const rows = await api(`/keywords?${p.toString()}`); setKws((Array.isArray(rows) ? rows : []).map(mapKw)); }
      catch { setKws([]); }
      finally { setLoading(false); setPaging(false); }
    }, [siteId, filterQS, page]);
    useEffect(() => { load(); }, [load]);

    /* Every count on this screen, over the whole filtered register.
     *
     * Not derived from `kws`, which is one page of it. The status tabs, the
     * count of what the fit filter is hiding, the unclassified count on
     * Classify and the total under the table all read from here, and a read
     * that failed leaves them unknown rather than zero: an empty register and a
     * count nobody could take are opposite findings.
     *
     * It does not depend on `page`, so walking through the table does not
     * recount anything. */
    const [sum, setSum] = useState({ loading: true, error: null, data: null });
    const loadSummary = useCallback(async () => {
      if (siteId == null) { setSum({ loading: false, error: null, data: null }); return; }
      setSum(s => ({ ...s, loading: true, error: null }));
      try { setSum({ loading: false, error: null, data: await api(`/keywords/summary?${filterQS().toString()}`) }); }
      catch (e) { setSum({ loading: false, error: e.message || "Could not count this register.", data: null }); }
    }, [siteId, filterQS]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    // A page past the end of the set shows nothing, and an empty table reads as
    // an empty register. It happens without anyone changing a filter: delete the
    // last row of the last page and the set is one page shorter than the page
    // you are standing on. The summary is what knows, so it is what corrects it.
    useEffect(() => {
      const t = sum.data && typeof sum.data.total === "number" ? sum.data.total : null;
      if (t == null) return;
      const last = Math.max(1, Math.ceil(t / PAGE_SIZE));
      if (page > last) setPage(last);
    }, [sum.data, page]);

    // Cheap enough for mount: it reads the page inventory the last scan stored
    // (site_pages), so it is a database read, not a crawl of the live site.
    const loadCov = useCallback(async () => {
      if (siteId == null) { setCov({ loading: false, error: null, data: null }); return; }
      setCov(c => ({ ...c, loading: true, error: null }));
      try { setCov({ loading: false, error: null, data: await api(`/coverage/keywords?site_id=${siteId}`) }); }
      // Kept as an error rather than as null data: a read that failed and a site
      // with no gaps must never render the same way.
      catch (e) { setCov({ loading: false, error: e.message || "Could not read the coverage picture.", data: null }); }
    }, [siteId]);
    useEffect(() => { loadCov(); }, [loadCov]);

    // What a run would classify INTO, read before anything is spent. A database
    // read, so it is cheap enough for mount. The states stay apart on purpose,
    // per facet: a read that failed, a server that answered without that facet
    // at all, and a site that genuinely has none of it - the last is a real
    // answer (a site whose posts are all Uncategorised has no categories, and
    // no keyword here has ever carried a cluster), and only an empty CATEGORY
    // vocabulary means the run would invent its own buckets.
    const [vocab, setVocab] = useState({ loading: true, error: null, data: null });
    const loadVocab = useCallback(async () => {
      if (siteId == null) { setVocab({ loading: false, error: null, data: null }); return; }
      setVocab(v => ({ ...v, loading: true, error: null }));
      try { setVocab({ loading: false, error: null, data: readVocab(await api(`/classify/vocabulary?site_id=${siteId}`)) }); }
      catch (e) { setVocab({ loading: false, error: e.message || "Could not read what this site classifies into.", data: null }); }
    }, [siteId]);
    useEffect(() => { loadVocab(); }, [loadVocab]);

    // Rows, counts, signals and coverage are the same facts read four ways, so
    // anything that writes refreshes all of them: deleting a keyword changes
    // how many the register holds and how many of them nothing targets. The
    // counts are in that list because they no longer come from the rows: a
    // write that refreshed the page but not the summary would leave the tabs
    // describing the register as it was. Mount is deliberately not a bump: the
    // strip loads itself.
    const reload = useCallback(async () => { await load(); await loadSummary(); await loadCov(); setInvKey(n => n + 1); }, [load, loadSummary, loadCov]);

    /* A PER ROW WRITE IS ADDRESSED TO THE SECTION THAT HOLDS THE ROW.
     *
     * The register READS at the market; the writes stay per connection, which
     * is why this takes the row and not just its id. At the All position the
     * table lists rows of every section, and sending the anchor's id for a row
     * of another section would answer "Keyword not found" with nothing on
     * screen explaining it. `k.siteId` null means the server did not say which
     * section, and the anchor is the honest fallback: it is what every request
     * from this screen used before the register had a position. */
    const ownerOf = (k) => (k && k.siteId != null ? k.siteId : siteId);

    const setKwStatus = async (k, val) => {
      const id = k.id;
      const owner = ownerOf(k);
      setKws(ks => ks.map(x => x.id === id ? { ...x, status: val } : x));   // optimistic
      if (owner == null) return;
      // The strip and the status tabs both count by status, so a stored change
      // has to reach them even though the table already painted it
      // optimistically. The tabs are counted by the server now, so an optimistic
      // row alone would leave them a keyword behind.
      try { await api(`/keywords/${id}?site_id=${owner}`, { method: "PATCH", body: JSON.stringify({ status: KW_STATUS_OUT[val] || "raw" }) }); setInvKey(n => n + 1); loadSummary(); } catch { load(); }
    };
    const del = async (k) => {
      const owner = ownerOf(k);
      if (owner == null) return;
      try { await api(`/keywords/${k.id}?site_id=${owner}`, { method: "DELETE" }); await reload(); } catch { /* */ }
    };

    /* A person overruling the classifier, which is the whole point of marking
       rather than deleting.
       POST /classify/fit stamps the reason with the mark every AI run checks
       for, so the correction is durable: the next run leaves the row alone
       instead of quietly putting its own verdict back. "Not judged" clears the
       verdict entirely and hands the row back to the classifier, which is the
       honest thing to do with one a person cannot decide either.
       Optimistic, then reconciled from the server on failure, the way
       setKwStatus already is: the toast is what says whether it landed. */
    const setFit = async (k, val) => {
      const next = val === "none" ? null : val;
      if (next === k.fit && (next === null || k.fitByHand)) return;
      setKws(ks => ks.map(x => x.id === k.id
        ? { ...x, fit: next, fitWhy: null, fitByHand: next != null }
        : x));
      const owner = ownerOf(k);
      if (owner == null) return;
      try {
        await api(`/classify/fit`, { method: "POST", body: JSON.stringify({ site_id: owner, kind: "keyword", ids: [k.id], fit: next }) });
        setInvKey(n => n + 1);
        // A verdict changes how many rows the fit filter is hiding, and that
        // number is on screen. It is the server's to recount.
        loadSummary();
        showToast(next
          ? `"${k.kw}" is ${FIT_LABEL[next].toLowerCase()}. A classify run will not overwrite a verdict you set.`
          : `Verdict cleared: "${k.kw}" is unjudged again, so the next classify run will judge it.`);
      } catch (e) {
        showToast(e.message || "That verdict could not be saved.", true);
        load();
      }
    };

    /* THE FILTERS, AS THE SERVER'S OWN WORDS FOR THEM.
     *
     * The same URLSearchParams that drew the rows and counted the summary, minus
     * the tenancy id the request body carries at its top level. Reusing
     * filterQS() is the whole guarantee: "1,991 matching" on the button is the
     * summary's count over these params, and POST /keywords/bulk-action resolves
     * these params through the same filterParts() the summary counted with. Any
     * second description of the set here and the number a person read would stop
     * being the number of rows that get deleted. */
    const matchFilter = () => { const p = filterQS(); p.delete("site_id"); return Object.fromEntries(p); };

    /**
     * One bulk act, on one of two sets, reported by what it DID.
     *
     * `target` is "selection" (the ids ticked, wherever in the register they
     * sit) or "matching" (every row the current filters match). Both go to one
     * request now. They used to be a loop of `await api()` per id, so a 2,000
     * row act was 2,000 requests, and there was no way at all to name a set
     * larger than the ids the browser was holding: that is why the only honest
     * label the bar could carry was "Select page (50)".
     *
     * The old loop had no catch either, so a single 404 partway through threw
     * out of the click handler, leaving the register half changed with nothing
     * on screen saying so. Everything below reports itself.
     *
     * `shown` is the number the pressed button had printed on it, and it is sent
     * as `expected_count` for the matching target only. The server refuses when
     * the register matches a different number: a filter is a DESCRIPTION and can
     * mean two thousand rows a moment after it meant fifty, which on a delete is
     * unrecoverable. A selection is already enumerated and cannot grow that way,
     * and guarding it would only deadlock somebody holding an id another tab
     * deleted, since reloading does not clear a selection.
     */
    const runBulk = async (target, action, shown) => {
      if (siteId == null || bulkBusy) return;
      const onSelection = target === "selection";
      const asked = onSelection ? sel.length : shown;
      if (!asked) return;
      const del = action === "delete";
      const label = (STATUS_OPTS.find(([v]) => v === bulkStat) || [null, bulkStat])[1];
      setBulkBusy(true);
      try {
        const res = await api(`/keywords/bulk-action`, {
          method: "POST",
          body: JSON.stringify({
            site_id: siteId,
            action : del ? "delete" : "set_status",
            ...(del ? {} : { status: KW_STATUS_OUT[bulkStat] || "raw" }),
            ...(onSelection ? { ids: sel } : { filter: matchFilter(), expected_count: asked }),
          }),
        });
        setSel([]); setSelectMode(false);
        await reload();
        // Two numbers, never one. `matched` is what the set held when the
        // statement ran and `affected` is what it changed, and they part company
        // exactly when the register moved underneath, which is the case worth
        // putting on screen rather than the one worth rounding away.
        const matched  = Number(res && res.matched) || 0;
        const affected = Number(res && res.affected) || 0;
        showToast(
          (del ? `Deleted ${plural(affected, "keyword", "keywords")}` : `${plural(affected, "keyword", "keywords")} set to ${label}`)
          + (matched !== affected
            ? `, of the ${num(matched)} matching when it ran: the register changed while it was running.`
            : affected < asked
              ? `, of the ${num(asked)} you selected: the rest were already gone from the register.`
              : "."),
        );
      } catch (e) {
        // A refused guard is not a failure to act, it is the act being STOPPED
        // because the set is no longer the one that was counted. Both numbers
        // are named, because "it changed" without them is nothing anybody can
        // decide on.
        const d = (e && e.data) || {};
        showToast(typeof d.matched === "number"
          ? `Nothing was changed. You were shown ${num(d.expected)} and ${num(d.matched)} match now, so this stopped rather than act on a set you have not seen.`
          : (e.message || "That bulk action failed."), true);
      } finally { setBulkBusy(false); }
    };

    // Classify a named set of ids, and report the run as what it was.
    //
    // The server caps a call at 60 and answers with three numbers, not one:
    // `classified` landed, `skipped` are rows inside the batch the AI reply was
    // cut short before reaching (left with their old facets), and `remaining`
    // are ids past the cap that were never sent. They go to a report that stays
    // on screen, not to a toast that fades, because a run that touched 12 of 200
    // leaves work to do and the screen has to still be saying so afterwards.
    //
    // What "continue" acts on is derived from the response rather than assumed:
    // a row the server sent back carrying a format is done, anything else in the
    // list is not, and ids past the cap are not in the response at all so they
    // stay in the set. Whatever else in the register is still untriaged follows
    // behind them, so one register can be finished from this one control.
    //
    // Nothing loops. Each run is a paid AI call, so the next one is a press.
    //
    // `rejudge` re-runs rows that already carry their facets, so the register
    // re-buckets against the current vocabulary. It is deliberately sent
    // WITHOUT ids: the server lifts its pending-only filter and takes its own
    // batch, and restricting it to the ids this screen counted as unclassified
    // would exclude exactly the rows it exists to re-run. A caller that names
    // its ids (a coverage signal, the continue button) keeps them and never
    // sends the flag: the count its button promised is the set that runs.
    const classify = async (ids, runOpts = {}) => {
      if (siteId == null || classifying) return;
      const list = [...new Set((ids || []).map(Number).filter(Boolean))];
      // The flag only ever travels without ids. A run that named its set is
      // acting on a promise already made, so the checkbox cannot widen it.
      const rejudge = !list.length && !!(runOpts && runOpts.rejudge);
      // Asked for an empty set. Nothing was sent, nothing failed, and saying
      // either would be wrong, so it gets its own outcome.
      if (!list.length && !rejudge) { setRunReport({ empty: "No keyword in this set is missing its facets, so nothing was sent to the AI.", classified: 0, next: [] }); return; }
      setClassifying(true);
      try {
        const res = await api(`/classify/keywords`, { method: "POST", body: JSON.stringify({
          site_id: siteId,
          ...(list.length ? { ids: list } : {}),
          ...(rejudge ? { rejudge: true } : {}),
        }) });
        await reload();
        const rows = (res && Array.isArray(res.rows)) ? res.rows : [];
        // The register held none of these ids. Retrying the same list would ask
        // for the same nothing, so the report offers no continuation for them.
        // A re-judge run named no ids, so empty rows mean an empty register
        // rather than a stale one, and the reload advice would be wrong for it.
        const matchedNothing = list.length > 0 && rows.length === 0 && !Number(res.remaining);
        const done = new Set(rows.filter(r => r.format).map(r => Number(r.id)));
        const untouched = matchedNothing ? [] : list.filter(id => !done.has(id));
        const carried = new Set(untouched);
        // The rest of the register, asked of the server rather than sifted out
        // of the rows in hand: the table now holds one page, so a continuation
        // built from it would offer the next 50 of 1,900 and call it the rest.
        // `unclassified=1` is the route's name for the predicate
        // routes/classify.js picks its own default batch with, so what this
        // offers to continue with is what a run would take.
        const others = (await fetchIds({ unclassified: "1" })).filter(id => !carried.has(id));
        setRunReport({
          error     : null,
          classified: Number(res.classified) || 0,
          skipped   : Number(res.skipped) || 0,
          remaining : Number(res.remaining) || 0,
          // A re-judge run without ids does not know its own size until the
          // server answers, so the batch it reports back is the count: never a
          // number invented here.
          asked     : list.length || rows.length + (Number(res.remaining) || 0),
          matchedNothing,
          // What the RUN says it classified into, not what this screen read
          // earlier. null means the server did not report one, which the report
          // says nothing about rather than calling it an empty vocabulary.
          vocab     : readVocab(res),
          // The belonging half of the same run, straight from the response and
          // never reconstructed here. A server that did not report it leaves
          // this null, and the report stays silent instead of claiming a sweep.
          fit       : (res && res.fit) || null,
          next      : [...untouched, ...others],
        });
        // Whether a run can add to what the next one sees is the server's
        // business, so the copy on screen is re-read rather than assumed stale
        // or assumed current.
        loadVocab();
      } catch (e) {
        // The server's messages are written for the user (an unreadable reply,
        // a missing API key, a batch too big to come back whole), so they are
        // shown verbatim and they stay until dismissed.
        setRunReport({ error: e.message || "Classification failed.", classified: 0, next: [] });
      } finally { setClassifying(false); }
    };

    // What the Classify button starts. The ids come from the server, by the same
    // predicate that produced the count on the button, so the run acts on the
    // set the button priced. Reading them is a request that can fail, and a
    // failure says so instead of quietly classifying the page in view.
    const classifyUnclassified = async () => {
      if (siteId == null || classifying) return;
      try { await classify(await fetchIds({ unclassified: "1" })); }
      catch (e) { setRunReport({ error: e.message || "Could not read which keywords are still unclassified.", classified: 0, next: [] }); }
    };

    // What "Plan these" does here, and why it does not create plans. One plan per
    // keyword is wrong for a batch, and one plan holding sixty is worse: both
    // decide how the work splits, which is the one judgement the signal cannot
    // make. So the rows are shown and selected, and the split stays with the user.
    //
    // The selection is a list of ids and the bulk actions act on ids, so it is
    // kept whole rather than trimmed to the rows in view. It used to be filtered
    // against the loaded register, which was every row; against one page of
    // fifty that same line would silently drop most of a signal's keywords and
    // report the remainder as the selection.
    const selectFromSignal = (ids) => {
      const want = [...new Set((ids || []).map(Number).filter(Boolean))];
      if (!want.length) { showToast("That signal named no keyword this screen can select.", true); return; }
      // Otherwise the selection lands on filtered-out rows, and the fit filter
      // is the one most likely to be hiding them: a coverage signal can easily
      // name a keyword the classifier called off-market.
      setStatus("all"); setUsage("all"); setSearch(""); setQ(""); setFitView("all"); setClusterF("all"); setFormatF("all"); setCategoryF("all"); setPage(1);
      setSelectMode(true); setSel(want);
      showToast(`Selected ${plural(want.length, "keyword", "keywords")}. The table shows ${PAGE_SIZE} at a time, so some of them are on later pages: the bulk actions act on everything you selected, not only on what is visible.`);
    };

    // Keywords → Planner. The plan lands as an idea the user renames; mark_used
    // is what flips the keyword to in_use server-side, so we reload afterwards
    // and let the row show the status the database actually stored.
    const promote = async (k) => {
      if (siteId == null || promoting != null) return;
      setPromoting(k.id);
      try {
        await api(`/content-plans`, {
          // The plan belongs to the section that holds the keyword. A plan is
          // section scoped ("a plan that cannot say where the piece will be
          // published is not a plan"), so a row of Blog (fr) promoted from the
          // All position must not create a plan on the Main Site.
          method: "POST",
          body: JSON.stringify({
            site_id: ownerOf(k), title: k.kw, primary_keyword: k.kw,
            status: "idea", source: "research", source_ref: `keyword:${k.id}`,
            mark_used: { keywords: [k.kw] },
          }),
        });
        await reload();
        showToast(`Content plan created: "${k.kw}" (rename it in the Planner)`);
      } catch (e) {
        showToast(e.message || "Could not create the content plan", true);
      } finally { setPromoting(null); }
    };

    // "From Search Console" imports the real per-query rows GSC reports for this
    // site (/gsc/performance?dimension=query, aggregated from the synced
    // gsc_stats). The duplicate guard is POST /keywords/bulk, which compares
    // against every keyword the site holds and reports what it skipped: the
    // guard used to be the loaded library, and now that the table holds one page
    // of fifty, a client-side check would have seen almost nothing already in
    // the register and re-added the rest. Every state that yields nothing is
    // named instead of silently reporting an import that never happened.
    const importFromGsc = async () => {
      if (siteId == null || gscBusy) return;
      setGscBusy(true);
      try {
        const st = await api(`/gsc/status?site_id=${siteId}`);
        if (!st.configured) { showToast(st.oauthAvailable ? "Search Console is not connected yet. Connect it on SEO · Performance." : "Search Console is not set up on this server yet. See SEO · Performance.", true); return; }
        if (!st.property) { showToast("No Search Console property is mapped to this site. Map one on SEO · Performance.", true); return; }
        const res = await api(`/gsc/performance?site_id=${siteId}&dimension=query&range=3m&limit=250`);
        const queries = ((res && res.rows) || []).map(r => String(r.key || "").trim()).filter(Boolean);
        if (!queries.length) { showToast(st.rows ? "No Search Console queries in the last 3 months." : "No Search Console data synced yet. Run a sync on SEO · Performance.", true); return; }
        const r = await api(`/keywords/bulk`, { method: "POST", body: JSON.stringify({ site_id: siteId, keywords: queries.map(kw => ({ keyword: kw })) }) });
        await reload();
        const added = Number(r && r.inserted) || 0;
        const dupes = Number(r && r.duplicates) || 0;
        // The same scope word the import view uses, from the same field, so the
        // two places that report a duplicate count cannot describe two
        // different registers.
        const where = (r && r.duplicates_scope) === "market" ? "this website's register" : "this section's register";
        showToast(added
          ? `Added ${plural(added, "keyword", "keywords")} of the ${queries.length} from Search Console${dupes ? `, ${dupes} already in ${where}` : ""}.`
          : `All ${queries.length} Search Console queries are already in ${where}.`);
      } catch (e) {
        showToast(e.message || "Search Console import failed", true);
      } finally { setGscBusy(false); }
    };

    // The scan behind the coverage picture. Slow, because it pages through every
    // published post and page over the REST API, so it reports the server's own
    // milestones through the run registry rather than a bar on a timer.
    //
    // What it reports back is what the response actually says: pages read of the
    // site's real total, keywords that matched a page, and whether the run hit
    // its ceiling. A run that read nothing is flagged as a failure, because
    // "scanned 0 pages" is not a success worth a green toast.
    const syncCoverage = async () => {
      if (siteId == null || covSyncing) return;
      setCovSyncing(true); setCovRun(null); setCovErr(null);
      const runId = newRunId();
      covStop.current = pollRun(runId, setCovRun);
      try {
        const res = await api(`/coverage/sync`, { method: "POST", body: JSON.stringify({ site_id: siteId, run_id: runId }) });
        covStop.current();
        await loadCov();
        setInvKey(n => n + 1);   // the strip's coverage signals read the same rows
        const read  = Number(res.scanned) || 0;
        const total = Number(res.site_total) || 0;
        const hit   = Number((res.matched || {}).keyword) || 0;
        const parts = [`Read ${read} of ${total} published page${total === 1 ? "" : "s"}`, `${hit} keyword${hit === 1 ? "" : "s"} matched a page`];
        // The server writes its own sentence for an empty or capped scan, so use
        // it rather than saying the same thing twice in different words.
        if (res.note) parts.push(String(res.note).replace(/\.$/, ""));
        else if (res.capped) parts.push(`stopped at the ${res.cap} page ceiling, so this picture is partial`);
        showToast(parts.join(". ") + ".", read === 0);
      } catch (e) {
        if (typeof covStop.current === "function") covStop.current();
        // The endpoint's messages are written for the user (unreachable host,
        // refused credentials, no known site URL), so they are shown verbatim
        // and they stay on screen instead of vanishing with the toast.
        const msg = e.message || "The coverage scan failed.";
        setCovErr(msg); showToast(msg, true);
      } finally { setCovSyncing(false); }
    };

    // The strip already headlines cannibalisation from the same coverage rows.
    // Rather than answer it twice, its button points at the panel that lists
    // which pages are competing, which is the part the strip cannot show.
    const revealCoverage = () => {
      setCovOpen(true);
      if (covRef.current) covRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (view === "import") return <ImportView onBack={() => setView("table")} site={site} onSaved={reload} />;
    if (view === "manual") return <ManualView onBack={() => setView("table")} site={site} onSaved={reload} />;
    // The register seen by cluster. Opening a cluster applies the table's own
    // Cluster filter, so what "view keywords" shows is exactly what the bulk
    // actions and the summary would act on.
    if (view === "clusters") return <ClustersView site={site} go={go} marketId={marketId} onKeywords={(c) => { if (c !== undefined) setClusterF(c); setView("table"); }} />;
    // The third reading of the same register: where two pieces target one term
    // in one language. It answers at the same POSITION the table does, so the
    // switcher governs it exactly as it governs everything else here.
    if (view === "overlap") return <OverlapView onBack={() => setView("table")} site={site} sectionId={sectionId} atAll={atAll} marketId={marketId} />;

    /* EVERY COUNT ON THIS SCREEN, AND WHERE IT COMES FROM.
     *
     * All of it used to be arithmetic over `kws`, which was the entire register
     * held in memory. `kws` is now one page of fifty, so counting it would turn
     * each of these into "of the rows you can see" without changing a word on
     * screen. They come from GET /api/keywords/summary instead, which counts in
     * the database over the same filters the rows were drawn with.
     *
     * `by_status` is counted with the status filter dropped, because those
     * numbers sit ON the status tabs and every tab but the selected one would
     * read zero otherwise. `by_fit` is counted with the fit filter dropped, for
     * the same reason in reverse: the strip has to say how many rows that filter
     * is HIDING, which is not a question the filter can answer about itself.
     * `register` is the site with no filter at all.
     *
     * null everywhere means the summary could not be read. It is rendered as "?"
     * and never as 0: a register with no keywords and a count nobody could take
     * are opposite findings, and the failure is stated in its own strip. */
    const S        = sum.data || null;
    const byStatus = S && S.by_status;
    const byFit    = S && S.by_fit;
    /* WHAT EACH SECTION IS, from the server rather than from a second list this
       screen keeps. A row carries a site_id and this is what turns it into a
       name; a section the server could not name renders as its id, because an id
       is still something a person can look up and a blank is not. */
    const sections = (S && Array.isArray(S.sections)) ? S.sections : null;
    const sectionName = (id) => {
      const hit = sections && sections.find(x => Number(x.site_id) === Number(id));
      return hit ? (hit.name || hit.url || null) : null;
    };
    /* WHICH POSITION THE SERVER ACTUALLY ANSWERED AT, which is not always the
       one asked for. A property with no market, with two, or whose rows the
       market backfill has not reached keeps today's per connection answer, and
       the server says so in a sentence. The screen must show the position it
       GOT, never the one it asked for, or a customer standing at All would read
       one section's register as the whole website's. */
    const answeredAll   = !!(S && S.position === "market");
    const positionWhy   = (S && S.read_position && S.read_position.why) || null;
    const wideningRefused = atAll && !!S && !answeredAll;
    /* A total over rows from several connections owes a breakdown, and this is
       it, counted over the same clause so it adds up to the total exactly. */
    const bySection = (S && Array.isArray(S.by_section)) ? S.by_section : null;
    const counts = {
      all      : byStatus ? byStatus.total : null,
      pending  : byStatus ? byStatus.raw : null,
      ready    : byStatus ? byStatus.classified : null,
      "in-use" : byStatus ? byStatus.in_use : null,
      archived : byStatus ? byStatus.archived : null,
    };
    // The filtered set the table is paging through, the site's whole register,
    // and what a classify run would still take.
    const total         = S ? S.total : null;
    const registerTotal = S && S.register ? S.register.total : null;
    const unclassified  = S && S.register ? S.register.unclassified : null;
    /* WHICH MARKET THE SERVER ANSWERED FOR, and it is the server's answer and
       never the one the control asked for: a market can be asked for and
       refused, and showing the asked-for market over rows that came from one
       connection is the quiet lie this screen spends its length avoiding. */
    const marketAnswer = (S && S.market) || null;
    const marketWide   = !!(marketAnswer && marketAnswer.selected != null);
    /* DOES THIS ANSWER SPAN MORE THAN ONE SECTION, which is the question the
       Section and Language columns really turn on. It used to be read off the
       switcher (`atAll`), and that was the whole answer while the only wide
       position was All. A selected market is wider than one connection from ANY
       position, so a row from another section would otherwise render with
       nothing saying which section holds it, and MODULE-SCOPE 1.1 is explicit:
       a wider view ENUMERATES. A row that came from a union over three
       connections and does not say which one holds it is a row nobody can act
       on. */
    const enumerates   = answeredAll || marketWide;
    /* THE ANCHOR'S OWN REGISTER, for the controls that WRITE.
       The list reads at the market; a classify run, an import, a coverage scan
       and a Search Console pull run on ONE CONNECTION, and the ids they take
       come from the anchor alone. So the moment the list is wider than the
       anchor, the acts are labelled with the anchor's figures: a button saying
       "Classify (1,610)" that then classifies 4 rows is the number-and-act
       mismatch that this whole screen is written against. */
    const anchorReg      = (S && S.anchor) || null;
    const widerThanActs  = marketWide && !atAll && !!anchorReg;
    const actUnclassified = widerThanActs ? anchorReg.unclassified : unclassified;
    const actRegisterTotal = widerThanActs ? anchorReg.total : registerTotal;
    // On the control itself, so a figure and the act beside it cannot be read
    // as being about the same set when they are not.
    const actScopeNote = widerThanActs
      ? "This runs on the section in the switcher, not on the whole market, and the count is that section's."
      : undefined;
    /* What the fit filter is hiding, added up from HIDDEN_FITS so the list the
       filter uses and the count of what it hides stay one list.
       This line used to read
         const hiddenByFit = beforeFit.length - rows.length;
       which subtracted one array of rows from another. That was a true count of
       what the filter hid only while this screen held the entire register in
       memory. Over a page of fifty it would say "3 hidden" on a register hiding
       1,400, and the sentence beneath it would still read "they are still in the
       register", which is the kind of quietly wrong number this screen exists to
       not produce. */
    const hiddenByFit   = byFit ? HIDDEN_FITS.reduce((n, f) => n + (byFit[f] || 0), 0) : null;
    const pageCount     = total == null ? null : Math.max(1, Math.ceil(total / PAGE_SIZE));

    // How many published pages target one keyword, from the scan rather than
    // from the row. The endpoint's lists are capped at 60, so the scan cannot
    // always prove an answer for a given keyword, and where it cannot this
    // returns null ("not known") rather than 0 ("no page"). The difference
    // matters: 0 is a finding, null is an absence of evidence.
    //   null = not known   0 = no page   1 = one page   -1 = at least one
    const cd = cov.data;
    const covScanned = !!cd && !cd.never_scanned;
    const looseIds  = new Set(((cd && cd.keywords_unattached) || []).map(k => Number(k.id)));
    const looseWhole = !!cd && ((cd.keywords_unattached || []).length >= (cd.keywords_unattached_total || 0));
    const cannBy    = new Map(((cd && cd.cannibalised) || []).map(c => [Number(c.ref_id), (c.posts || []).length]));
    const cannWhole = !!cd && ((cd.cannibalised || []).length >= (cd.cannibalised_total || 0));
    /* A ROW OF ANOTHER SECTION HAS NO COVERAGE EVIDENCE HERE, and this guard is
       the one that keeps the All position honest.
       A coverage scan reads ONE CONNECTION'S published pages, so `cd` describes
       the anchor and nothing else. Without this line a keyword of Blog (fr)
       seen from Main Site would fall through to the "targeted" branch at the
       bottom: absent from the anchor's cannibalised list, absent from the
       anchor's unattached list, and therefore reported as a keyword a published
       page targets, which the scan never looked at. Null is "not known", which
       is what it is. This is the same mistake that listed 90 pages holding most
       of a website's clicks under nothing: a display rule keyed on evidence
       that did not cover the rows it was applied to. */
    const coveredSection = (k) => k.siteId == null || siteId == null || Number(k.siteId) === Number(siteId);
    const pagesFor = (k) => {
      if (!covScanned) return null;
      if (!coveredSection(k)) return null;
      if (cannBy.has(k.id)) return cannBy.get(k.id);
      if (looseIds.has(k.id)) return 0;
      // Absence from a capped list is not proof, and the unattached list is
      // drawn from the non-archived register, so an archived row missing from
      // it proves nothing either.
      if (!looseWhole || k.status === "archived") return null;
      return cannWhole ? 1 : -1;             // targeted; the exact count only when nothing was capped
    };
    const pagesCell = (k) => {
      const n = pagesFor(k);
      if (n === null) return <span className="kw-used kw-used--unused" title={coveredSection(k)
        ? "No coverage scan has answered this either way. Sync coverage to find out."
        : "This keyword belongs to another section of the website, and a coverage scan reads one section's published pages. Switch to that section to see its coverage."}>unknown</span>;
      if (n === 0)  return <span className="kw-used kw-used--unused" title="No published page targets this keyword.">no page</span>;
      if (n === -1) return <span className="kw-used" title="At least one published page targets this keyword. The exact count was beyond what this scan returned.">targeted</span>;
      if (n === 1)  return <span className="kw-used">1 page</span>;
      return <span className="kw-used" style={{ color: "var(--danger)" }} title="These pages compete with each other for this keyword.">{n} pages</span>;
    };

    /* The page as the server served it. Status, fit, search and High priority
       are query params now, so none of them is re-applied here: the rows that
       arrived are the rows that matched, and the total beside them counts the
       same set.

       The two coverage segments are the exception and cannot be anything else.
       "No page" and "Targeted" are answers from the last coverage SCAN, not
       columns in the register, and the scan returns its lists capped, so there
       is nothing for the query to filter on. They therefore narrow the fifty
       rows in hand, and the line under the toolbar says so rather than letting
       the range read as though the whole register had been searched. */
    let rows = kws;
    if (usage === "available") rows = rows.filter(k => pagesFor(k) === 0);
    else if (usage === "used") rows = rows.filter(k => { const n = pagesFor(k); return n !== null && n !== 0; });
    // Counted here, before the guard below, so it says what the COVERAGE
    // segment took off this page and nothing else.
    const pageOnlyFilter = usage === "available" || usage === "used";
    const narrowedOnPage = kws.length - rows.length;
    // The fit guard, not the fit filter: the route already applied that, and
    // these rows passed it. This only catches a verdict set on the row a moment
    // ago, which is written optimistically and would otherwise sit in a view
    // that claims to hide it until the next page load. It removes nothing
    // otherwise, and it is never counted: the strip's number is the summary's.
    rows = rows.filter(k => fitMatches(k, fitView));
    const filtered = status !== "all" || usage !== "all" || fitView !== "all" || clusterF !== "all" || formatF !== "all" || categoryF !== "all" || !!q;

    // What the numbers on the status tabs are counting, which is not obvious
    // once a fit filter or a search is on: they count the whole register under
    // the OTHER filters, so "Pending" under "Fit: relevant only" is every
    // pending keyword judged relevant, not every pending keyword and not the
    // pending ones on this page.
    const otherFilters = [
      fitView !== "all" ? "the Fit filter" : "",
      q ? `the search for "${q}"` : "",
      usage === "high" ? "High priority" : "",
      clusterF !== "all" ? (clusterF === "__none__" ? "No cluster yet" : `the "${clusterF}" cluster`) : "",
      formatF !== "all" ? "the Format filter" : "",
      categoryF !== "all" ? "the Category filter" : "",
    ].filter(Boolean);
    const countsTitle = sum.error ? `These could not be counted: ${sum.error}`
      : otherFilters.length
        ? `Counted by the server over every keyword in this register that passes ${otherFilters.join(" and ")}. Not the rows on this page.`
        : "Counted by the server over every keyword in this register, not over the rows on this page.";

    // One row of the register.
    const kwRow = (k) => {
      return (
        <tr key={k.id} className={k.src === "research" ? "is-captured-row" : ""}>
          {selectMode && <td><label className="hds-check"><input type="checkbox" checked={sel.includes(k.id)} onChange={() => toggle(k.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label></td>}
          <td><span className="kw-kw">
            {k.kw}
            {k.src === "research" && <span className="kw-src-badge" title="Captured from Research">research</span>}
          </span></td>
          {/* WHICH SECTION HOLDS IT, only at the All position, where the rows
              come from several connections. MODULE-SCOPE 1.1: a wider view
              enumerates. At one section this column would repeat the switcher on
              every row. A section the legend could not name shows its id, which
              is still something a person can act on. */}
          {enumerates && <td className="kw-num" style={{ color: "var(--text-secondary)" }}>
            {k.siteId == null ? <span title="The server did not say which section holds this row">-</span> : (sectionName(k.siteId) || `#${k.siteId}`)}
          </td>}
          {/* The STORED language, shown at All because that is where a reader
              needs it: two rows carrying one term in two languages are
              alternates, and the only thing that says so is this column. A row
              with none says so rather than showing a blank that reads as a
              language nobody noticed. */}
          {enumerates && <td className="kw-num">
            {k.lang
              ? <span className="cluster-tag" title={`Stored language: ${k.lang}`}>{langLabel(k.lang)}</span>
              : <span style={{ color: "var(--text-disabled)" }} title="No language is recorded on this keyword. The overlap view cannot tell a translation pair from a real overlap without one.">none</span>}
          </td>}
          <td className="kw-num">{k.vol.toLocaleString()}</td>
          <td className="kw-gsc">{k.impr > 0 ? <span title={k.clicks ? `${k.clicks} clicks · CTR ${((k.ctr != null ? k.ctr : k.clicks / k.impr) * 100).toFixed(1)}%` : "No clicks in window"}><b>{k.impr.toLocaleString()}</b> · {k.pos.toFixed(1)}</span> : <span style={{ color: "var(--text-disabled)" }}>-</span>}</td>
          <td><span className={"intent-badge " + intentCls[k.intent]}>{k.intent.slice(0, 4)}</span></td>
          <td><span className="cluster-tag">{k.format}</span></td>
          <td className="kw-num" style={{ color: "var(--text-secondary)" }}>{k.cat}</td>
          <td><span className={"priority-badge priority-" + k.prio}>{k.prio}</span></td>
          <td><span className="cluster-tag">{k.cluster}</span></td>
          {/* Editable in place, like Status, because the verdict has to
              be arguable where it is shown. The hover carries the one
              sentence the classifier gave for it. */}
          <td><div title={fitTitle(k)}>
            <FD label="Fit" value={k.fit || "none"} opts={FIT_OPTS} colorOf={v => FIT_COLOR[v] || FIT_COLOR.none} down onChange={val => setFit(k, val)} />
          </div></td>
          <td><FD label="Status" value={k.status} opts={STATUS_OPTS} colorOf={v => STATUS_COLOR[v] || "var(--text-muted)"} down onChange={val => setKwStatus(k, val)} /></td>
          <td>{pagesCell(k)}</td>
          <td><div className="kw-actions">
            <button className="bi-iconbtn" title="Promote to a content plan" disabled={promoting != null} onClick={() => promote(k)}>{promoting === k.id ? <span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} /> : <I name="arrow-right" size={16} />}</button>
            <button className="bi-iconbtn" title="Write article" onClick={() => onWrite && onWrite(k.kw)}><I name="edit" size={16} /></button>
            <button className="bi-iconbtn" title="Delete" onClick={() => del(k)}><I name="x" size={16} /></button>
          </div></td>
        </tr>
      );
    };
    // The same four answers the dialog gives, per facet, short enough to hover.
    // It never says "none" for a read that failed or for a facet the server did
    // not report, and it leads with the cluster for the same reason the dialog
    // does. Tags are left to the dialog: a hover is not the place for the
    // weakest of the three.
    const vocabTitle = vocab.loading ? "Reading what this site classifies into"
      : vocab.error ? `Could not read what this site classifies into: ${vocab.error}`
      : [["cluster", "clusters", (vocab.data || {}).clusters], ["category", "categories", (vocab.data || {}).categories]]
          .map(([one, many, f]) => {
            switch (facetState(f)) {
              case "some": return `${plural(f.total, one, many)} to classify into`;
              case "none": return one === "cluster"
                ? "no cluster exists yet, so this run writes the first ones"
                : "no categories, so a run would invent a bucket per keyword";
              case "failed": return `the ${many} could not be read`;
              default: return `the server did not say which ${many}`;
            }
          }).join(" · ");

    return (
      <div className="bi-page">
        <div className="bi-header">
          <div className="bi-header__titles"><h1 className="bi-title"><span className="bi-title__icon"><I name="target" size={20} /></span>Keywords Planner</h1></div>
          <div className="bi-header-actions">
            {/* The acts that WRITE, or spend tokens, run on ONE CONNECTION, so
                they are unavailable at the All position and say why. A keyword
                is captured into one section's register and a coverage scan reads
                one section's published pages; running either from a position
                that shows three sections would act on the anchor while the
                screen showed the website, which is the quietly wrong set. */}
            <button className="bi-btn" disabled={blocked} title={perSectionOnly} onClick={() => { setSelectMode(s => !s); setSel([]); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn" disabled={blocked} title={perSectionOnly} onClick={() => setView("import")}><I name="upload-cloud" size={16} />Import Excel</button>
            <button className="bi-btn" disabled={blocked} title={perSectionOnly} onClick={() => setView("manual")}><I name="plus" size={16} />Manual</button>
            <button className="bi-btn" disabled={blocked || covSyncing || siteId == null} title={perSectionOnly} onClick={syncCoverage}>{covSyncing ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />Scanning…</> : <><I name="refresh" size={16} />Sync coverage</>}</button>
            {/* Re-running facets over keywords that already have them is still
                Keywords 2 work. It says so rather than opening a dialog that
                warns about tokens for a run that never happens. */}
            {/* Re-cluster IS re-analyze-all now: the classify dialog with the
                rejudge box pre-checked. The toast that said "not wired up yet"
                outlived the wiring. */}
            <button className="bi-btn bi-btn--ai" disabled={blocked || classifying || siteId == null || !actRegisterTotal} title={perSectionOnly || actScopeNote} onClick={() => { setConfirmRejudge(true); setConfirm("classify"); }}><I name="sparkles" size={16} />Re-analyze all</button>
          </div>
        </div>
        <p className="research-intro">Your site's keyword register: every keyword, in use or not, with what the live site actually does with them. Keywords captured from Research carry a <b>research</b> badge; classify, plan and write from here.</p>
        {/* The two readings of one register: by keyword, by cluster. A view
            switch, never a filter: nothing is added or removed by it. */}
        <div className="kw-seg" style={{ marginBottom: 12 }}>
          <button className="on">Keywords</button>
          <button onClick={() => setView("clusters")}>Clusters</button>
          {/* A THIRD READING, in the selector the other two already live in.
              Not a sidebar entry: a new kind of an existing thing belongs in
              that thing's existing selector, and two entries for one subject is
              how two screens come to disagree about what the website is. */}
          <button title="Where two pieces target the same term in the same language. Computed from what you planned, not from Search Console." onClick={() => setView("overlap")}>Overlap</button>
        </div>

        {/* The coverage picture, computed over every published page the last
            scan stored rather than over the rows this table holds. A site with
            214 pages and 41 keyword rows used to say nothing about the other
            173 pages; this is where it does. */}
        {/* WHICH SCOPE THESE NUMBERS AND ROWS ARE. Shown only at the All
            position, because at one section it would restate the switcher.
            It reports the position the SERVER answered at, not the one asked
            for: a property with no market, with two, or whose rows the market
            backfill has not reached keeps today's per connection answer, and
            reading that as the whole website is exactly the quiet lie this
            screen spends its length avoiding. */}
        {/* THE MARKET CONTROL. Draws nothing on a website with fewer than two
            markets, which is most of them, so those screens are unchanged. */}
        <MarketControl markets={markets} marketId={marketId} onPick={onPickMarket}
          answer={marketAnswer} noun="keyword" />
        {/* AND WHAT THE ACTS RUN ON, when the list is wider than they are. The
            same shape as the coverage note below it, which has said the
            equivalent about one section's published pages for as long as this
            screen has had a position. */}
        {widerThanActs && (
          <div className="ds-strip ds-strip--quiet">
            <I name="info" size={14} />
            <span>
              The rows above are this market's, across every section that holds any. The acts that write run on
              <b> {sectionName(siteId) || "this section"}</b> alone, which is the section in the switcher, so their
              counts are its own: Classify would take {actUnclassified == null ? "an unknown number of" : num(actUnclassified)} of
              its {actRegisterTotal == null ? "" : num(actRegisterTotal) + " "}keywords. Pick another section in the
              switcher to act on it.
            </span>
          </div>
        )}
        {atAll && answeredAll && (
          <div className="ds-strip ds-strip--quiet">
            <I name="layers" size={14} />
            <span>
              The whole website's keyword register: one market shares one register, so every section's keywords are
              here and each row names the section that holds it.
              {bySection && bySection.length > 1
                ? <> {bySection.map(r => `${sectionName(r.site_id) || "#" + r.site_id}: ${num(r.count)}`).join(" · ")}.</>
                : null}
              {" "}Nothing is merged, so a term two sections both hold appears twice. Acts that write run on one section
              at a time: pick one in the switcher.
            </span>
          </div>
        )}
        {wideningRefused && (
          <div className="ds-strip ds-strip--warning">
            <I name="alert-triangle" size={14} />
            <span>
              This is <b>one section</b>, not the whole website, even though the switcher is at "The whole website".
              {positionWhy ? ` ${positionWhy}` : ""}
            </span>
          </div>
        )}

        {covErr && (
          <div className="ds-strip ds-strip--error">
            <I name="alert-triangle" size={14} />
            <span>Coverage scan failed: {covErr}</span>
            <button className="bi-btn bi-btn--sm" disabled={covSyncing} onClick={syncCoverage}>Try again</button>
          </div>
        )}
        {/* THE COVERAGE PANEL IS ONE SECTION'S, ALWAYS. It is computed from the
            published pages the last scan of this CONNECTION stored, so at the
            All position it is the anchor's and the line above it says so. The
            alternative was hiding it, which would take away the one place that
            says which published pages target which keyword. */}
        {enumerates && siteId != null && (
          <div className="ds-strip ds-strip--quiet">
            <I name="info" size={14} />
            <span>
              The coverage figures below are <b>{sectionName(siteId) || "this section"}</b>'s published pages, not the
              whole website's: a coverage scan reads one section's CMS. Pick that section in the switcher to act on
              them.
            </span>
          </div>
        )}
        {siteId != null && <CoveragePanel
          cov={cov} open={covOpen} setOpen={setCovOpen} panelRef={covRef}
          onRetry={loadCov} onSync={syncCoverage} syncing={covSyncing} run={covRun}
          onPromote={promote} promoting={promoting} onSelect={selectFromSignal} />}

        {/* What the table is not saying: rows nothing has triaged or planned. It
            sits above the filters because it is context for the whole screen, and
            it only offers actions this screen can honestly perform. The cluster
            signal's "Show them" is absent on purpose: there is no cluster filter
            here to drive, and a button that moved nothing would be a lie. */}
        {window.MlDiscovery && <window.MlDiscovery.DiscoveryStrip
          siteId={siteId} module="keywords" reloadKey={invKey}
          actions={{
            classify: (sig) => classify(sig.ids), plan: (sig) => selectFromSignal(sig.ids),
            // The strip's cannibalisation card counts the same coverage rows the
            // panel above does, so its button goes there rather than restating
            // the finding a third time. Which pages are competing is the part
            // only the panel can show.
            "keywords-cannibalised": revealCoverage,
          }} />}

        {/* The counts could not be read. Said in its own strip because the
            numbers below would otherwise read as a register that is empty, and
            because the table underneath is still perfectly usable: the rows
            arrived, only the counting failed. */}
        {sum.error && (
          <div className="ds-strip ds-strip--error">
            <I name="alert-triangle" size={14} />
            <span>Could not count this register: {sum.error} The numbers below show "?" rather than a zero, and the table is still showing real rows.</span>
            <button className="bi-btn bi-btn--sm" onClick={loadSummary}>Try again</button>
          </div>
        )}

        {/* Counted by the database over the whole register, not over the page in
            view. The hover says which filters each number is under, because
            "Pending 1,904" means something different with a fit filter on. */}
        <div className="bi-stat-cards" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
          {[["all", "All status", "var(--primary)", counts.all], ["pending", "Pending", "var(--warning)", counts.pending], ["ready", "Ready", "var(--success)", counts.ready], ["in-use", "In use", "var(--info)", counts["in-use"]], ["archived", "Archived", "var(--text-muted)", counts.archived]].map(([id, l, c, v]) => (
            <button key={id} className={"bi-stat-card bi-stat-card--clickable" + (status === id ? " bi-stat-card--active" : "")} title={countsTitle} onClick={() => setStatus(id)}>
              <div className="bi-stat-card__top"><span className="bi-stat-card__dot" style={{ background: c }} /><span className="bi-stat-card__label">{l}</span></div>
              <span className="bi-stat-card__value">{num(v)}</span>
            </button>
          ))}
        </div>

        {/* The last run, sitting directly above the control that started it.
            Only after a run: there is deliberately no standing banner here,
            because a register with nothing left to classify has nothing to say
            and a permanent nudge would be nagging rather than informing. */}
        {runReport && <ClassifyRunReport run={runReport} busy={classifying} onContinue={classify} onDismiss={() => setRunReport(null)} onTaxonomy={goTaxonomy} />}

        <div className="kw-toolbar">
          {/* "No page" and "Targeted" are coverage answers, so they are dead
              until a scan has produced one. Disabled with a reason beats a
              filter that silently returns nothing. */}
          <div className="kw-seg">{[["all", "All", null], ["available", "No page", "needs a coverage scan"], ["used", "Targeted", "needs a coverage scan"], ["high", "High priority", null]].map(([id, l, need]) => <button key={id} className={usage === id ? "on" : ""} disabled={!!need && !covScanned} title={need && !covScanned ? need : undefined} onClick={() => setUsage(id)}>{l}</button>)}</div>
          {/* Live facet filters, server-applied: the summary count and the bulk
              actions' "all N matching" follow them because they ride the same
              filterQS. Cluster and category options come from the vocabulary
              read the Classify dialog already makes (the site's real values,
              not a hardcoded guess, which is what sat here before, wired to
              nothing). "No X yet" is a real set: it is what Classify acts on. */}
          <FD label="Cluster" value={clusterF} opts={[["all", "Cluster: all"], ["__none__", "No cluster yet"], ...(((vocab.data && vocab.data.clusters && vocab.data.clusters.names) || []).map(n => [n, n]))]} colorOf={() => "var(--text-muted)"} down onChange={setClusterF} />
          <FD label="Format" value={formatF} opts={[["all", "Format: all"], ["__none__", "No format yet"], ["how-to", "How-to"], ["guide", "Guide"], ["listicle", "Listicle"], ["comparison", "Comparison"], ["faq", "FAQ"], ["informational", "Informational"], ["news", "News"]]} colorOf={() => "var(--text-muted)"} down onChange={setFormatF} />
          <FD label="Category" value={categoryF} opts={[["all", "Category: all"], ["__none__", "No category yet"], ...(((vocab.data && vocab.data.categories && vocab.data.categories.names) || []).map(n => [n, n]))]} colorOf={() => "var(--text-muted)"} down onChange={setCategoryF} />
          {/* The one filter that hides rows by default, so it is a control the
              user can see and change rather than a rule applied behind them. */}
          <FD label="Fit" value={fitView} opts={FIT_VIEW_OPTS} colorOf={v => FIT_COLOR[v] || FIT_COLOR.none} down onChange={setFitView} />
          {/* The count is the SERVER's, over the whole register: keywords with
              no facets or no fit verdict, which is the predicate
              routes/classify.js picks its own default batch with. It used to
              count rows with status Pending, which is neither what the run takes
              (a row moved back to Pending by hand keeps its facets) nor what the
              dialog beside it already promised. A count nobody could read
              disables the button and says why rather than offering a run priced
              at nothing. */}
          {/* A register with nothing left to classify must still reach the
              re-cluster run: that is the whole recovery path for clusters a
              retired feature overwrote. So a fully classified register keeps
              the button enabled, opens the dialog with re-cluster pre-checked
              (the only run that would do anything), and the label drops the
              "(0)" that would price the run at nothing. */}
          {/* THE COUNT ON THIS BUTTON IS THE COUNT IT WILL ACT ON. `actUnclassified`
              is the anchor's whenever the list is wider than the anchor, which
              is what makes the figure and the run the same set. */}
          <button className="bi-btn bi-btn--sm bi-btn--ai" title={[perSectionOnly, actScopeNote, (actUnclassified == null ? `How many keywords still need classifying could not be read${sum.error ? ": " + sum.error : ""}` : vocabTitle)].filter(Boolean).join(" ")} disabled={blocked || classifying || siteId == null || actUnclassified == null || (!actUnclassified && !actRegisterTotal)} onClick={() => { setConfirmRejudge(!actUnclassified); setConfirm("classify"); }}>{classifying ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />Classifying…</> : <><I name="sparkles" size={14} />{actUnclassified ? `Classify (${num(actUnclassified)})` : "Re-cluster"}</>}</button>
          <button className="bi-btn bi-btn--sm" disabled={blocked || gscBusy || siteId == null} title={perSectionOnly} onClick={importFromGsc}>{gscBusy ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />Importing…</> : <><I name="search" size={14} />From Search Console</>}</button>
          <div style={{ flex: 1 }} />
          <div className="kw-toolbar__search"><div className="hds-input-group"><span className="hds-input-group__icon"><I name="search" size={16} /></span><input className="hds-input" placeholder="Search keywords" value={search} onChange={e => setSearch(e.target.value)} /></div></div>
        </div>

        {/* What this view is not showing. A screen that silently withholds 1,400
            rows is worse than one that shows them, so the number is stated and
            the way back is one press. It says "hidden" and not "removed"
            deliberately: nothing here deletes a keyword on a verdict.

            The number is the summary's, counted over the whole register with the
            fit filter dropped and the others kept. Counting it from the rows in
            hand would say "3 hidden" on every page of a register hiding 1,400,
            which is the failure this whole change exists to avoid. */}
        {fitView === "keep" && hiddenByFit > 0 && (
          <div className="ds-strip ds-strip--quiet">
            <I name="eye" size={14} />
            <span>
              {plural(hiddenByFit, "keyword is", "keywords are")} hidden here as off market or off topic, across the whole
              register and not just this page. They are still in the register, and a verdict you disagree with can be
              changed in the Fit column.
            </span>
            <button className="bi-btn bi-btn--sm" onClick={() => setFitView("all")}>Show them</button>
          </div>
        )}

        {/* A coverage segment is the one filter that cannot be the server's:
            "No page" is an answer from the last scan, not a column in the
            register. It narrows the page in hand, so it says so rather than
            letting the range below read as a search of everything. */}
        {pageOnlyFilter && (
          <div className="ds-strip ds-strip--quiet">
            <I name="info" size={14} />
            <span>
              {usage === "available" ? "\"No page\"" : "\"Targeted\""} comes from the last coverage scan rather than from the
              register, so it narrows the {plural(kws.length, "row", "rows")} on this page
              {narrowedOnPage > 0 ? ` to ${rows.length}` : ""}, not the whole filtered set. The count above the table is
              the set before it.
              {enumerates ? " A scan reads one section's published pages, so rows belonging to the other sections have no coverage answer and this segment leaves them out rather than guessing one." : ""}
            </span>
          </div>
        )}

        {/* TWO SETS, AND EVERY CONTROL SAYS WHICH ONE IT IS.
            The page in hand, and every row the filters match. Both are real acts
            now: the server resolves a filter into a set and applies one
            statement to it, so "all" can be offered without a control claiming a
            register-wide act and performing a page-wide one. That claim is the
            bug this whole bar was rebuilt to close, so nothing here is ever
            labelled "all" when it means the fifty rows on screen, and nothing is
            labelled with a bare count that could be read as either.

            "Select page" still ADDS to the selection rather than replacing it,
            so walking through several pages builds one set and a selection made
            from a coverage signal is not thrown away by pressing it.

            The matching controls need a number to be honest, and the summary is
            what has it. A total nobody could read disables them and says why:
            "all" over an unknown quantity is not a claim this screen can make. */}
        {selectMode && (
          <div className="bi-bulk-bar">
            <span className="bi-bulk-bar__count">{sel.length} selected</span>
            <button className="bi-btn bi-btn--sm" title={`Adds the ${rows.length} keywords on this page to the selection`} onClick={() => setSel(s => [...new Set([...s, ...rows.map(k => k.id)])])}><I name="check-square" size={14} />Select page ({rows.length})</button>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
            <span className="bi-bulk-bar__sep" />
            <FD label="Set status" value={bulkStat} opts={STATUS_OPTS} colorOf={v => STATUS_COLOR[v] || "var(--text-muted)"} down onChange={setBulkStat} />
            <button className="bi-btn bi-btn--sm" disabled={!sel.length || bulkBusy} title={`Sets the status of the ${plural(sel.length, "keyword", "keywords")} you ticked, on this page or any other`} onClick={() => runBulk("selection", "set_status")}>Set status on {sel.length} selected</button>
            <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={!sel.length || bulkBusy} title={`Deletes the ${plural(sel.length, "keyword", "keywords")} you ticked and nothing else`} onClick={() => runBulk("selection", "delete")}><I name="trash" size={14} />Delete {sel.length} selected</button>
            <span className="bi-bulk-bar__sep" />
            <button className="bi-btn bi-btn--sm" disabled={!total || bulkBusy}
              title={typeof total !== "number"
                ? `How many keywords these filters match could not be counted${sum.error ? ": " + sum.error : ""}, so this cannot say what it would act on`
                : `Sets the status of every keyword these filters match, all ${num(total)} of them, not only the ${rows.length} on this page`}
              onClick={() => runBulk("matching", "set_status", total)}>Set status on all {num(total)} matching</button>
            <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={!total || bulkBusy}
              title={typeof total !== "number"
                ? `How many keywords these filters match could not be counted${sum.error ? ": " + sum.error : ""}, so this cannot say what it would delete`
                : `Deletes every keyword these filters match, all ${num(total)} of them. This cannot be undone.`}
              onClick={() => setConfirm("delete-matching")}><I name="trash" size={14} />Delete all {num(total)} matching</button>
            <span className="ds-card__note">
              {bulkBusy ? "Working through the set, in one request rather than one per keyword" : <>
                The selected buttons act on the {plural(sel.length, "keyword", "keywords")} you ticked, wherever in the register they
                sit. The matching buttons act on every row these filters match, which is the count stated under the table.
              </>}
            </span>
          </div>
        )}

        {/* The rows dim while the next page is fetched instead of being
            replaced by a loading state: a table that empties on every Next reads
            as a register that lost its keywords. */}
        <div className="kw-table-wrap" style={paging && kws.length ? { opacity: 0.55 } : undefined}>
          <table className="kw-table">
            <thead><tr>
              {selectMode && <th style={{ width: 36 }}></th>}
              <th>Keyword</th>
              {enumerates && <th title="Which section of this website holds this keyword. The register is shared across the whole market, so a wider view names every row's section.">Section</th>}
              {enumerates && <th title="The language recorded on this keyword. It is a stored fact: nothing here derives it from the market or from the business profile.">Language</th>}
              <th>Volume</th><th>GSC impr·pos</th><th>Intent</th><th>Format</th><th>Category</th><th>Priority</th><th>Cluster</th><th title="Whether this keyword belongs to this business at all. Set it yourself to overrule the classifier: a verdict you set is never overwritten by a later run.">Fit</th><th>Status</th><th title="Published pages targeting this keyword, from the last coverage scan">Pages</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {rows.map(kwRow)}
            </tbody>
          </table>
          {/* An empty table under a filter is not an empty register, and saying
              "add some manually" to someone who has 200 keywords and a narrow
              filter on would be wrong. */}
          {loading ? <div className="bi-subempty">Loading keywords…</div> : rows.length === 0 && <div className="bi-subempty">{filtered ? `No keywords match these filters. The register holds ${num(registerTotal)}.` : "No keywords found. Add some manually."}</div>}

          {/* WHERE THIS PAGE SITS IN THE FILTERED SET.
              The total is the server's count of everything the filters match,
              never the rows in hand, so "51 to 100 of 1,991" stays true on a
              table that only ever holds fifty. A total nothing could read says
              so rather than quietly becoming the number of rows on screen. */}
          {!loading && kws.length > 0 && (
            <div className="ml-pagination">
              <span>
                {total == null
                  ? `Showing ${plural(kws.length, "keyword", "keywords")}: how many match these filters could not be counted`
                  : <>Showing {num((page - 1) * PAGE_SIZE + 1)} to {num((page - 1) * PAGE_SIZE + kws.length)} of {num(total)}{filtered ? " matching" : ""} {total === 1 ? "keyword" : "keywords"}</>}
                {narrowedOnPage > 0 ? ` · ${narrowedOnPage} of them hidden on this page by the coverage filter` : ""}
                {paging ? " · loading" : ""}
              </span>
              <div className="ml-pagination__pages">
                <button className="bi-btn bi-btn--sm" disabled={page <= 1 || paging} onClick={() => setPage(p => Math.max(1, p - 1))}><I name="chevron-left" size={14} />Prev</button>
                <span className="ds-card__note">{pageCount == null ? `Page ${page}` : `Page ${page} of ${pageCount}`}</span>
                <button className="bi-btn bi-btn--sm" disabled={paging || (pageCount != null ? page >= pageCount : kws.length < PAGE_SIZE)} onClick={() => setPage(p => p + 1)}>Next<I name="chevron-right" size={14} /></button>
              </div>
            </div>
          )}
        </div>

        {/* What the run costs, what it unblocks, and what it will classify
            INTO, all stated before the tokens are spent. The number of runs is
            arithmetic on the server's own cap, not an estimate; the vocabulary
            is whatever the server says it will use, facet by facet, or plainly
            nothing. .ds-brief gives the blocks their rhythm, so none of them
            carries a margin of its own. */}
        {confirm === "classify" && window.MlBiModals && <window.MlBiModals.ConfirmDialog
          title={confirmRejudge ? "Classify this register, re-clustering what already has facets?" : "Classify " + unclassified + " keywords?"}
          body={<div className="ds-brief">
            <div>
              {/* The count is the server's, and it is every keyword still
                  missing its facets OR its fit verdict, anywhere in this
                  register: not the ones on this page, and not the ones whose
                  status happens to say Pending. */}
              {confirmRejudge
                ? "Sends the unclassified keywords AND re-runs rows that already carry their cluster, category and fit verdict, so the register re-buckets against the current vocabulary. This uses tokens, including on rows that were already paid for once. One run still takes at most " + PER_RUN + "; the rest stay for the next one."
                : "Runs AI classification on the " + unclassified + " keywords in this register that have no facets or no fit verdict, "
                  + PER_RUN + " per run, so this takes "
                  + plural(Math.ceil(unclassified / PER_RUN), "run", "runs") + " and uses tokens each time. "
                  + "It is what makes them usable: facet assembly matches keywords into a piece by cluster or category, "
                  + "and an unclassified keyword has neither, so pieces promoted from topics stay without a keyword until this runs."}
            </div>
            {/* Re-clustering is the way a register classified under an OLD
                vocabulary catches up with the current one, and it is the only
                grouping story here: the cluster the run writes is what facet
                assembly reads. Unchecked by default every time: spending tokens
                twice on the same rows is a choice, not a preference to
                remember. A classify run a signal starts with its own ids never
                reads this box. */}
            {/* With nothing left to classify, re-cluster is the only run this
                dialog can offer, so the choice is not shown as one. */}
            {unclassified > 0 ? (
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <span className="hds-check"><input type="checkbox" checked={confirmRejudge} onChange={e => setConfirmRejudge(e.target.checked)} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
                <span>Also re-cluster rows already classified</span>
              </label>
            ) : (
              <div>Everything here already carries facets, so this run is a re-cluster: the whole register re-buckets against the current vocabulary.</div>
            )}
            {confirmRejudge && <div className="ds-card__note">Re-clustering re-runs rows that already carry clusters and facets so they re-bucket against the current vocabulary. Those rows cost tokens again. A fit verdict you set by hand is still left alone.</div>}
            {/* Said before the run because it is the half that decides what stays
                on screen afterwards. The verdict is only reachable when the site
                has a business profile to judge against, and the run says which
                of those two happened rather than this screen guessing now. */}
            <div>
              It also judges whether each keyword belongs to this business at all, against your business profile, and
              marks it relevant, off market, off topic or unclear. <b>Nothing is deleted or archived on that verdict:</b> off-market
              and off-topic keywords are hidden by the Fit filter, which says how many it is hiding, and any verdict can
              be changed on the row. A keyword you judged yourself is left alone. With no business profile on file
              nothing is judged at all, and the run will say so rather than guess.
            </div>
            <VocabBrief vocab={vocab} onTaxonomy={goTaxonomy} />
          </div>}
          confirmLabel={confirmRejudge ? "Classify and re-cluster" : "Classify"} onClose={() => setConfirm(null)}
          onConfirm={() => { setConfirm(null); if (confirmRejudge) classify(null, { rejudge: true }); else classifyUnclassified(); }} />}

        {/* THE ONE ACT ON THIS SCREEN THAT CANNOT BE TAKEN BACK.
            It states the number, says which set that number is (not the page,
            not the ticked rows), and says plainly that nothing brings the rows
            back. The number is also what travels with the request: the server
            refuses if the register matches a different one, so the set this
            dialog describes is the set that gets deleted, or nothing is. */}
        {confirm === "delete-matching" && window.MlBiModals && <window.MlBiModals.ConfirmDialog
          danger
          title={`Delete ${num(total)} keywords?`}
          body={<div className="ds-brief">
            <div>
              This deletes <b>every keyword these filters match</b>, all {num(total)} of them: not the {rows.length} on
              this page, and not the {sel.length} you have ticked. <b>It cannot be undone.</b> A deleted keyword takes
              its facets and its fit verdict with it, and nothing here brings them back.
            </div>
            <div>
              The {num(total)} is counted by the server over these same filters, and it is sent with the request: if the
              register matches a different number by the time this runs, the delete is refused and says both, rather
              than acting on a set you have not seen.
            </div>
            {/* The one filter on this screen the server cannot apply, said here
                because this is the act where the gap could not be taken back.
                The count and the delete agree with each other, and both are
                wider than the rows the segment is leaving on screen. */}
            {pageOnlyFilter && (
              <div>
                <b>The coverage segment is not part of this set.</b> "No page" and "Targeted" are answers from the last
                coverage scan rather than columns in the register, so they narrow the rows on this page and never what
                the server matches. This deletes all {num(total)}, including the ones that segment is currently hiding
                from you.
              </div>
            )}
            <div>
              If the aim is to get off-market or off-topic keywords out of the way, <b>the Fit filter already hides
              them</b> and leaves the verdict arguable on the row. Deleting is for a bad import, where the rows are not
              this business's keywords at all.
            </div>
          </div>}
          confirmLabel={`Delete ${num(total)}`} onClose={() => setConfirm(null)}
          onConfirm={() => { setConfirm(null); runBulk("matching", "delete", total); }} />}

        {toast && <div className="inbox-toast"><I name={toast.bad ? "alert-triangle" : "check-circle"} size={16} style={toast.bad ? { color: "var(--danger)" } : undefined} />{toast.msg}</div>}
      </div>
    );
  }

  /* MarketControl is exported because the Topics Bank renders the same control
   * over the same declarations, and two implementations of one control is how
   * two screens come to describe one website two ways. app/research-topics.jsx
   * resolves it at RENDER time, so neither module depends on import order. */
  window.MlResearchKeywords = { KeywordsPlanner, MarketControl };
})();
